export type ServicePageIntent =
  | "transactional"
  | "price"
  | "use_case"
  | "system_type"
  | "service"
  | "advice"
  | "rules";

export type ServicePageCategory =
  | "money"
  | "room"
  | "system"
  | "price_offer"
  | "maintenance"
  | "advice"
  | "rules";

export interface ServicePageFaq {
  question: string;
  answer: string;
}

export interface ServicePageSource {
  label: string;
  url: string;
}

export interface ServicePage {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  intentType: ServicePageIntent;
  category: ServicePageCategory;
  primaryKeyword: string;
  secondaryKeywords: string[];
  intro: string;
  benefits: string[];
  whyTitle: string;
  whyBody: string;
  costTitle: string;
  costBody: string;
  processTitle: string;
  attentionTitle: string;
  attentionPoints: string[];
  faq: ServicePageFaq[];
  sourceLinks?: ServicePageSource[];
  ctaVariant: "offerte" | "advies" | "spoed" | "onderhoud";
  relatedSlugs: string[];
  canonicalSlug: string;
  indexable: boolean;
}

export const servicePages: ServicePage[] = [
  {
    slug: "airco-laten-plaatsen-limburg",
    title: "Airco laten plaatsen Limburg",
    metaTitle: "Airco laten plaatsen Limburg: checklist voor uw woning",
    metaDescription: "Bereid de plaatsing van uw airco in Limburg voor. Bekijk de checklist voor units, leidingwerk, bereikbaarheid en werkzaamheden; vraag StayCool advies.",
    h1: "Airco laten plaatsen in Limburg: bereid uw woning voor",
    intentType: "transactional",
    category: "money",
    primaryKeyword: "Airco laten plaatsen Limburg",
    secondaryKeywords: ["airco plaatsen Limburg", "airco montage Limburg", "airco installateur Limburg"],
    intro: "Waar kan de buitenunit komen en hoe bereiken de leidingen de binnenunit? Deze checklist helpt u uw woning voor te bereiden op een airco-installatie. Noteer welke ruimtes u wilt koelen of verwarmen en bespreek plaatsing en afwerking met StayCool vóór u een afspraak maakt.",
    benefits: ["Checklist voor uw woning","Plaatsing vooraf bespreken","Offerte met installatiescope"],
    whyTitle: "Welke informatie helpt bij een passend voorstel?",
    whyBody: "Foto’s van de gewenste binnen- en buitenpositie, de afmetingen van de ruimte en informatie over isolatie helpen om uw vraag te beoordelen. De uiteindelijke plaatsing hangt ook af van geluid, onderhoudstoegang, condensafvoer en de mogelijkheden van de woning.",
    costTitle: "Wat kost een airco laten plaatsen in Limburg?",
    costBody: "De prijs hangt af van het type systeem, het aantal ruimtes, leidingwerk en montageomstandigheden. Een offerte op maat voorkomt aannames en geeft direct inzicht in de totale investering.",
    processTitle: "Hoe werkt het aanvragen van een offerte?",
    attentionTitle: "Waar moet je op letten?",
    attentionPoints: ["Kies vermogen op basis van ruimte en isolatie.", "Controleer of montage en leidingwerk zijn inbegrepen.", "Let op geluidsniveau van binnen- en buitenunit.", "Vraag naar garantie en onderhoud na plaatsing."],
    faq: [
      { question: "Hoe snel krijg ik reactie op mijn aanvraag?", answer: "Meestal ontvang je persoonlijk advies met de eerste vervolgstap of prijsindicatie." },
      { question: "Is een offerte aanvragen verplicht?", answer: "Nee, de aanvraag is vrijblijvend. Je beslist zelf of je verder wilt met het voorstel." },
      { question: "Kan een airco ook verwarmen?", answer: "Ja, veel moderne split airco's kunnen naast koelen ook energiezuinig verwarmen." },
      { question: "Voor welke woningen is dit geschikt?", answer: "Voor appartementen, rijwoningen, twee-onder-een-kapwoningen en vrijstaande woningen in Limburg." },
    ],
    ctaVariant: "offerte",
    relatedSlugs: ["airco-installatie-limburg", "airco-installatie-prijs-limburg", "airco-offerte-aanvragen-limburg"],
    canonicalSlug: "airco-laten-plaatsen-limburg",
    indexable: true,
  },
  {
    slug: "airco-installatie-limburg",
    title: "Airco installatie Limburg",
    metaTitle: "Airco installatie Limburg: voorbereiding en montage",
    metaDescription: "Hoe verloopt een airco-installatie in Limburg? Lees wat we vooraf beoordelen, hoe montage werkt en welke informatie nodig is voor uw offerte.",
    h1: "Airco installatie in Limburg: van opname tot oplevering",
    intentType: "transactional",
    category: "money",
    primaryKeyword: "Airco installatie Limburg",
    secondaryKeywords: ["airco installeren Limburg", "airco montage Limburg", "split airco installatie Limburg"],
    intro: "Een passende airco kiezen is de eerste stap. Op deze pagina leest u hoe StayCool de installatie voorbereidt: de binnen- en buitenunit plaatsen, de leidingroute beoordelen en de werkzaamheden met u afspreken. Wilt u eerst modellen en prijzen vergelijken? Gebruik daarvoor onze aankoopgids en kostengids.",
    benefits: ["Opname van plaatsing en leidingroute","Werkzaamheden vooraf beschreven","Planning in overleg"],
    whyTitle: "Wat beoordelen we vóór de montage?",
    whyBody: "Tijdens de voorbereiding bespreken we de ruimtes, isolatie, zonbelasting en het gewenste gebruik. We bekijken de plaats van de units, leidingdoorvoer, condensafvoer en bereikbaarheid. Zo worden de werkzaamheden en mogelijke aanvullingen vooraf duidelijk.",
    costTitle: "Wat kost airco installatie in Limburg?",
    costBody: "De kosten verschillen per systeem. Single split is meestal voordeliger dan multi split. Extra leidinglengte, bereikbaarheid en afwerking bepalen de uiteindelijke offerte.",
    processTitle: "Van woningopname naar een concrete installatieafspraak",
    attentionTitle: "Waar moet je op letten bij installatie?",
    attentionPoints: ["Laat vermogen en plaatsing vooraf beoordelen.", "Vraag om een all-in overzicht van montagekosten.", "Controleer certificering en garantievoorwaarden.", "Bespreek planning en bereikbaarheid van de buitenunit."],
    faq: [
      { question: "Hoelang duurt een airco installatie?", answer: "Een standaard installatie duurt vaak een dagdeel tot een dag, afhankelijk van systeem en leidingwerk." },
      { question: "Moet er altijd een buitenunit komen?", answer: "Bij split airco's wel. Er bestaan ook oplossingen zonder buitenunit, maar die hebben andere voor- en nadelen." },
      { question: "Kan ik meerdere kamers tegelijk laten installeren?", answer: "Ja, met een multi split airco kunnen meerdere binnenunits op een buitenunit worden aangesloten." },
      { question: "Is de aanvraag gratis?", answer: "Ja, je vraagt vrijblijvend informatie en een offerte aan." },
    ],
    ctaVariant: "offerte",
    relatedSlugs: ["airco-laten-plaatsen-limburg", "single-split-airco-installatie-limburg", "multi-split-airco-installatie-limburg"],
    canonicalSlug: "airco-installatie-limburg",
    indexable: true,
  },
  {
    slug: "airco-kopen-en-laten-installeren-limburg",
    title: "Airco kopen en laten installeren Limburg",
    metaTitle: "Airco met installatie Limburg: offerte controleren",
    metaDescription: "Vergelijk airco-offertes inclusief installatie in Limburg. Controleer uitvoering, materialen, btw, aanvullingen en garantievoorwaarden.",
    h1: "Airco kopen met installatie in Limburg: uw offerte",
    intentType: "transactional",
    category: "money",
    primaryKeyword: "Airco kopen en laten installeren Limburg",
    secondaryKeywords: ["airco kopen Limburg", "airco inclusief installatie Limburg", "airco compleet laten plaatsen"],
    intro: "Een compleet voorstel maakt duidelijk welk toestel u koopt én welke werkzaamheden worden uitgevoerd. Deze pagina helpt u installatieoffertes op dezelfde onderdelen te vergelijken. Voor beschikbare modellen gaat u naar airco kopen in Limburg; de kostengids geeft gecontroleerde prijsvoorbeelden.",
    benefits: ["Exacte uitvoering vergelijken","Installatie en materialen benoemen","Btw en aanvullingen controleren"],
    whyTitle: "Vergelijk de complete uitvoering",
    whyBody: "Controleer de exacte modelcodes, het aantal binnenunits en het vermogen. Kijk daarna naar montage, materialen, btw, leidingroute, eventuele aanvullende werkzaamheden en afwerking. Laat de afgesproken garantievoorwaarden en planning schriftelijk opnemen. Een lager bedrag is pas vergelijkbaar wanneer dezelfde onderdelen zijn inbegrepen.",
    costTitle: "Wat kost airco kopen en installeren in Limburg?",
    costBody: "De totaalprijs bestaat uit het toestel, montage, leidingwerk, materialen en eventuele extra afwerking. Een complete offerte maakt de vergelijking eerlijker.",
    processTitle: "Hoe werkt een complete aanvraag?",
    attentionTitle: "Waar moet je op letten bij kopen en installeren?",
    attentionPoints: ["Vergelijk altijd totaalprijzen inclusief montage.", "Let op energielabel en geluidsniveau.", "Kies geen te licht of te zwaar toestel.", "Vraag naar garantie op toestel en installatie."],
    faq: [
      { question: "Kan ik zelf een airco kopen en laten plaatsen?", answer: "Dat kan soms, maar veel installateurs werken liever met toestellen die zij zelf leveren vanwege garantie en kwaliteit." },
      { question: "Welke merken zijn geschikt?", answer: "Dat hangt af van budget, ruimte en gebruik. Een installateur kan passende merken en modellen voorstellen." },
      { question: "Is goedkoper altijd beter?", answer: "Nee. Let ook op rendement, garantie, geluid en montagekwaliteit." },
      { question: "Krijg ik een all-in offerte?", answer: "Het doel is een duidelijke offerte voor toestel, montage en noodzakelijke materialen." },
    ],
    ctaVariant: "offerte",
    relatedSlugs: ["airco-inclusief-montage-limburg", "airco-installatie-prijs-limburg", "beste-airco-voor-woning-limburg"],
    canonicalSlug: "airco-kopen-en-laten-installeren-limburg",
    indexable: true,
  },
  {
    slug: "airco-voor-woonkamer-installeren-limburg",
    title: "Airco voor woonkamer installeren Limburg",
    metaTitle: "Airco woonkamer installeren Limburg",
    metaDescription: "Airco voor de woonkamer installeren in Limburg? Vraag vrijblijvend advies en ontvang snel een passende offerte.",
    h1: "Airco voor woonkamer installeren in Limburg",
    intentType: "use_case",
    category: "room",
    primaryKeyword: "Airco voor woonkamer installeren Limburg",
    secondaryKeywords: ["woonkamer airco Limburg", "airco woonkamer plaatsen", "airco woonruimte Limburg"],
    intro: "Een airco in de woonkamer moet krachtig genoeg zijn, maar ook stil en netjes geplaatst worden. Vraag een offerte aan voor een passende woonkamer airco in Limburg.",
    benefits: ["Comfort in de grootste leefruimte", "Advies over vermogen", "Vrijblijvende prijsindicatie"],
    whyTitle: "Waarom een airco in de woonkamer?",
    whyBody: "De woonkamer is vaak de ruimte waar warmte het meest merkbaar is. Een goed gekozen airco koelt gericht en kan in het voor- en najaar ook verwarmen.",
    costTitle: "Wat kost een woonkamer airco in Limburg?",
    costBody: "De prijs hangt af van oppervlakte, isolatie, gewenste positie en leidingwerk. Bij grotere woonkamers kan meer vermogen nodig zijn.",
    processTitle: "Hoe werkt de aanvraag voor een woonkamer airco?",
    attentionTitle: "Waar moet je op letten in de woonkamer?",
    attentionPoints: ["Bereken vermogen op basis van inhoud en zonbelasting.", "Kies een stille binnenunit.", "Let op luchtstroom richting zithoek.", "Bespreek kabel- en leidingafwerking."],
    faq: [
      { question: "Hoeveel vermogen heeft een woonkamer airco nodig?", answer: "Dat hangt af van oppervlakte, plafondhoogte, isolatie en zoninval. Daarom is een berekening belangrijk." },
      { question: "Kan de airco ook verwarmen?", answer: "Ja, veel woonkamer airco's kunnen efficient bijverwarmen." },
      { question: "Waar plaats je de binnenunit?", answer: "Meestal op een plek met vrije luchtstroom en zo min mogelijk directe tocht op zitplaatsen." },
      { question: "Is een woonkamer airco stil?", answer: "Moderne units zijn stil, maar het juiste model en de juiste standplaats blijven belangrijk." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-vermogen-berekenen-limburg", "airco-voor-slaapkamer-installeren-limburg", "single-split-airco-installatie-limburg"],
    canonicalSlug: "airco-voor-woonkamer-installeren-limburg",
    indexable: true,
  },
  {
    slug: "airco-voor-slaapkamer-installeren-limburg",
    title: "Airco voor slaapkamer installeren Limburg",
    metaTitle: "Airco slaapkamer installeren Limburg",
    metaDescription: "Airco voor de slaapkamer installeren in Limburg? Vraag vrijblijvend een offerte aan voor een stille oplossing.",
    h1: "Airco voor slaapkamer installeren in Limburg",
    intentType: "use_case",
    category: "room",
    primaryKeyword: "Airco voor slaapkamer installeren Limburg",
    secondaryKeywords: ["slaapkamer airco Limburg", "stille airco slaapkamer", "airco slaapkamer plaatsen"],
    intro: "Wil je beter slapen tijdens warme nachten? Vraag een vrijblijvende offerte aan voor een stille slaapkamer airco in Limburg, afgestemd op jouw ruimte en woning.",
    benefits: ["Stille oplossing", "Koeler slapen", "Passende montageplek"],
    whyTitle: "Waarom een airco in de slaapkamer?",
    whyBody: "Een slaapkamer airco verlaagt de temperatuur gericht en helpt om comfortabeler te slapen. Geluid, luchtstroom en vermogen zijn hier belangrijker dan maximale kracht.",
    costTitle: "Wat kost een slaapkamer airco in Limburg?",
    costBody: "De kosten hangen af van kamerformaat, plaatsing van de buitenunit en leidingroute. Voor slaapkamers is een compacte single split vaak voldoende.",
    processTitle: "Hoe werkt de aanvraag voor een slaapkamer airco?",
    attentionTitle: "Waar moet je op letten in de slaapkamer?",
    attentionPoints: ["Kies een model met lage geluidsstand.", "Voorkom directe luchtstroom op het bed.", "Let op dimbare displays en nachtmodus.", "Laat vermogen niet te hoog kiezen."],
    faq: [
      { question: "Is een airco in de slaapkamer niet te luid?", answer: "Een geschikt model met nachtmodus kan zeer stil werken, mits goed geplaatst." },
      { question: "Kan ik slapen met de airco aan?", answer: "Ja, met een rustige stand en goede temperatuurinstelling is dat meestal comfortabel." },
      { question: "Welke airco past bij een kleine slaapkamer?", answer: "Vaak volstaat een compact single split systeem met laag geluidsniveau." },
      { question: "Kan een slaapkamer airco ook ontvochtigen?", answer: "Ja, airco's halen vocht uit de lucht tijdens het koelen." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-voor-woonkamer-installeren-limburg", "airco-voor-zolder-installeren-limburg", "single-split-airco-installatie-limburg"],
    canonicalSlug: "airco-voor-slaapkamer-installeren-limburg",
    indexable: true,
  },
  {
    slug: "airco-voor-zolder-installeren-limburg",
    title: "Airco voor zolder installeren Limburg",
    metaTitle: "Airco zolder installeren Limburg",
    metaDescription: "Airco voor zolder installeren in Limburg? Ontvang snel advies en een vrijblijvende offerte voor jouw zolderruimte.",
    h1: "Airco voor zolder installeren in Limburg",
    intentType: "use_case",
    category: "room",
    primaryKeyword: "Airco voor zolder installeren Limburg",
    secondaryKeywords: ["zolder airco Limburg", "airco zolderkamer plaatsen", "airco dakverdieping Limburg"],
    intro: "Zolders worden snel warm en vragen om een slimme airco-oplossing. Vraag vrijblijvend een offerte aan voor een zolder airco in Limburg met passend vermogen en montageadvies.",
    benefits: ["Geschikt voor warme zolders", "Advies over vermogen", "Duidelijke montage-opties"],
    whyTitle: "Waarom een airco op zolder?",
    whyBody: "Een zolder heeft vaak veel zonbelasting en warmteopbouw. Met de juiste airco maak je een slaapkamer, kantoor of hobbyruimte weer bruikbaar in warme periodes.",
    costTitle: "Wat kost een zolder airco in Limburg?",
    costBody: "De prijs hangt af van de bereikbaarheid, dakconstructie, leidingroute en gewenste buitenunitpositie. Een opname voorkomt verrassingen.",
    processTitle: "Hoe werkt de aanvraag voor een zolder airco?",
    attentionTitle: "Waar moet je op letten op zolder?",
    attentionPoints: ["Controleer isolatie en zonbelasting.", "Bespreek buitenunitpositie op dak, gevel of grond.", "Let op condensafvoer.", "Kies voldoende vermogen voor piekwarmte."],
    faq: [
      { question: "Is zolder koelen moeilijker dan andere ruimtes?", answer: "Vaak wel, omdat zolders meer warmte vasthouden. De juiste capaciteitsberekening is belangrijk." },
      { question: "Kan de buitenunit op het dak?", answer: "Dat kan soms, afhankelijk van constructie, bereikbaarheid en geluidsregels." },
      { question: "Is een mobiele airco genoeg voor zolder?", answer: "Voor tijdelijk gebruik soms, maar een split airco is meestal stiller en efficienter." },
      { question: "Kan een zolder airco ook verwarmen?", answer: "Ja, veel systemen kunnen de zolder ook bijverwarmen." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-vermogen-berekenen-limburg", "airco-met-buitenunit-plaatsen-limburg", "airco-voor-slaapkamer-installeren-limburg"],
    canonicalSlug: "airco-voor-zolder-installeren-limburg",
    indexable: true,
  },
  {
    slug: "multi-split-airco-installatie-limburg",
    title: "Multi split airco installatie Limburg",
    metaTitle: "Multi split airco Limburg | Offerte",
    metaDescription: "Multi split airco installatie in Limburg? Vraag vrijblijvend een offerte aan voor meerdere kamers met een buitenunit.",
    h1: "Multi split airco installatie in Limburg",
    intentType: "system_type",
    category: "system",
    primaryKeyword: "Multi split airco installatie Limburg",
    secondaryKeywords: ["multi split airco Limburg", "airco meerdere kamers Limburg", "multi split installatie"],
    intro: "Wil je meerdere kamers koelen of verwarmen met een systeem? Vraag een vrijblijvende offerte aan voor multi split airco installatie in Limburg.",
    benefits: ["Meerdere kamers", "Een buitenunit mogelijk", "Duidelijke systeemkeuze"],
    whyTitle: "Waarom kiezen voor multi split?",
    whyBody: "Met multi split sluit je meerdere binnenunits aan op een buitenunit. Dat is handig als je woonkamer, slaapkamers of zolder apart wilt regelen.",
    costTitle: "Wat kost multi split airco in Limburg?",
    costBody: "De kosten hangen af van het aantal binnenunits, leidinglengtes, vermogen en montage. Multi split is duurder dan single split, maar vaak netter bij meerdere ruimtes.",
    processTitle: "Hoe werkt een multi split offerte?",
    attentionTitle: "Waar moet je op letten bij multi split?",
    attentionPoints: ["Bepaal per kamer het benodigde vermogen.", "Controleer of een buitenunit voldoende capaciteit heeft.", "Bespreek leidingroutes per ruimte.", "Vraag om duidelijke prijs per extra binnenunit."],
    faq: [
      { question: "Hoeveel kamers kan ik aansluiten?", answer: "Dat hangt af van het gekozen systeem en vermogen. Veel woningen gebruiken twee tot vijf binnenunits." },
      { question: "Kan elke kamer apart worden ingesteld?", answer: "Ja, binnenunits zijn meestal afzonderlijk te bedienen." },
      { question: "Is multi split goedkoper dan losse systemen?", answer: "Niet altijd, maar het kan praktisch en esthetisch beter zijn bij meerdere kamers." },
      { question: "Is een opname nodig?", answer: "Voor multi split is een goede opname sterk aan te raden vanwege leidingroutes en capaciteit." },
    ],
    ctaVariant: "offerte",
    relatedSlugs: ["single-split-airco-installatie-limburg", "airco-voor-slaapkamer-installeren-limburg", "airco-voor-woonkamer-installeren-limburg"],
    canonicalSlug: "multi-split-airco-installatie-limburg",
    indexable: true,
  },
  {
    slug: "single-split-airco-installatie-limburg",
    title: "Single split airco installatie Limburg",
    metaTitle: "Single split airco Limburg | Offerte",
    metaDescription: "Single split airco installatie in Limburg? Vraag vrijblijvend een offerte aan voor een kamer of ruimte.",
    h1: "Single split airco installatie in Limburg",
    intentType: "system_type",
    category: "system",
    primaryKeyword: "Single split airco installatie Limburg",
    secondaryKeywords: ["single split airco Limburg", "airco een kamer Limburg", "mono split airco installatie"],
    intro: "Een single split airco is vaak de beste keuze voor een woonkamer, slaapkamer of zolder. Vraag vrijblijvend een offerte aan voor installatie in Limburg.",
    benefits: ["Ideaal voor een ruimte", "Vaak snel geplaatst", "Duidelijke totaalprijs"],
    whyTitle: "Waarom kiezen voor single split?",
    whyBody: "Single split bestaat uit een binnenunit en een buitenunit. Het is overzichtelijk, efficient en geschikt als je een specifieke ruimte wilt koelen of verwarmen.",
    costTitle: "Wat kost single split installatie in Limburg?",
    costBody: "De prijs hangt af van het toestel, vermogen, leidinglengte en montageplek. Voor een ruimte is single split meestal de meest betaalbare vaste oplossing.",
    processTitle: "Hoe werkt een single split offerte?",
    attentionTitle: "Waar moet je op letten bij single split?",
    attentionPoints: ["Stem vermogen af op de ruimte.", "Kies een logische buitenunitpositie.", "Let op geluid en rendement.", "Controleer wat standaard in montage zit."],
    faq: [
      { question: "Voor hoeveel ruimtes is single split geschikt?", answer: "Voor een ruimte. Voor meerdere kamers is multi split vaak logischer." },
      { question: "Kan single split verwarmen?", answer: "Ja, moderne single split systemen kunnen meestal koelen en verwarmen." },
      { question: "Is single split goedkoper dan multi split?", answer: "Voor een kamer meestal wel, omdat er minder materiaal en montage nodig is." },
      { question: "Hoe snel kan single split geplaatst worden?", answer: "Dat hangt af van planning en situatie, maar standaard montage is vaak relatief snel." },
    ],
    ctaVariant: "offerte",
    relatedSlugs: ["multi-split-airco-installatie-limburg", "airco-installatie-prijs-limburg", "airco-voor-woonkamer-installeren-limburg"],
    canonicalSlug: "single-split-airco-installatie-limburg",
    indexable: true,
  },
  {
    slug: "airco-met-buitenunit-plaatsen-limburg",
    title: "Airco met buitenunit plaatsen Limburg",
    metaTitle: "Airco met buitenunit plaatsen Limburg",
    metaDescription: "Airco met buitenunit plaatsen in Limburg? Vraag vrijblijvend advies over montageplek, geluid en kosten.",
    h1: "Airco met buitenunit plaatsen in Limburg",
    intentType: "system_type",
    category: "system",
    primaryKeyword: "Airco met buitenunit plaatsen Limburg",
    secondaryKeywords: ["split airco buitenunit Limburg", "buitenunit plaatsen airco", "airco buitenunit montage"],
    intro: "Wil je een airco met buitenunit laten plaatsen in Limburg? Vraag een offerte aan en krijg duidelijkheid over positie, geluidsniveau, leidingwerk en montagekosten.",
    benefits: ["Advies over buitenunitpositie", "Duidelijke montagekosten", "Vrijblijvend aanvragen"],
    whyTitle: "Waarom een airco met buitenunit?",
    whyBody: "Een split airco met buitenunit is meestal stiller en efficienter dan mobiele oplossingen. De juiste plaatsing bepaalt comfort, geluid en levensduur.",
    costTitle: "Wat kost plaatsen met buitenunit in Limburg?",
    costBody: "De kosten hangen af van montageplek, beugels, leidingroute, afstand tussen units en bereikbaarheid. Buitenunitplaatsing kan de offerte sterk beinvloeden.",
    processTitle: "Hoe werkt de aanvraag voor buitenunit plaatsing?",
    attentionTitle: "Waar moet je op letten bij de buitenunit?",
    attentionPoints: ["Controleer geluidsafstand tot buren.", "Bespreek gevel-, dak- of grondplaatsing.", "Zorg voor onderhoudsbereikbaarheid.", "Vraag naar trillingsdempers en bevestiging."],
    faq: [
      { question: "Waar mag een buitenunit geplaatst worden?", answer: "Dat hangt af van woning, VvE, buren en lokale situatie. Een installateur beoordeelt de opties." },
      { question: "Maakt een buitenunit veel geluid?", answer: "Moderne buitenunits zijn relatief stil, maar positie en montage zijn bepalend." },
      { question: "Kan de buitenunit op een plat dak?", answer: "Vaak wel, mits constructie, bereikbaarheid en trillingsdemping goed zijn." },
      { question: "Is onderhoud aan de buitenunit nodig?", answer: "Ja, vrije luchtstroom en periodieke controle helpen storingen voorkomen." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-buitenunit-plaatsen-regels-limburg", "stille-airco-buitenunit-limburg", "airco-buitenunit-erfgrens-limburg"],
    canonicalSlug: "airco-met-buitenunit-plaatsen-limburg",
    indexable: true,
  },
  {
    slug: "airco-inclusief-montage-limburg",
    title: "Airco inclusief montage Limburg",
    metaTitle: "Airco inclusief montage Limburg",
    metaDescription: "Airco inclusief montage in Limburg? Ontvang een vrijblijvende all-in offerte met toestel, plaatsing en uitleg.",
    h1: "Airco inclusief montage in Limburg",
    intentType: "transactional",
    category: "price_offer",
    primaryKeyword: "Airco inclusief montage Limburg",
    secondaryKeywords: ["airco all-in Limburg", "airco met montage Limburg", "airco compleet pakket"],
    intro: "Zoek je een airco inclusief montage in Limburg? Vraag een complete offerte aan zodat je direct ziet wat toestel, installatie en afwerking samen kosten.",
    benefits: ["All-in prijsinzicht", "Geen losse onderdelen vergelijken", "Persoonlijk advies"],
    whyTitle: "Waarom kiezen voor inclusief montage?",
    whyBody: "Een all-in offerte voorkomt onduidelijkheid over materialen, leidingwerk en installatie. Je vergelijkt complete oplossingen in plaats van losse toestelprijzen.",
    costTitle: "Wat kost airco inclusief montage in Limburg?",
    costBody: "De totaalprijs hangt af van type airco, montageafstand, vermogen en extra werkzaamheden. Vraag altijd na welke onderdelen standaard inbegrepen zijn.",
    processTitle: "Hoe werkt een all-in offerte?",
    attentionTitle: "Waar moet je op letten bij inclusief montage?",
    attentionPoints: ["Controleer of leidingwerk en muurdoorvoer zijn inbegrepen.", "Vraag naar eventuele meerprijs per meter leiding.", "Let op garantie op montage.", "Vergelijk niet alleen toestelprijzen."],
    faq: [
      { question: "Wat betekent inclusief montage?", answer: "Dat toestel, standaardmaterialen en plaatsing samen in de offerte worden opgenomen." },
      { question: "Zijn extra meters leiding inbegrepen?", answer: "Dat verschilt per aanbieder. Vraag daarom altijd om een duidelijke specificatie." },
      { question: "Is inclusief montage voordeliger?", answer: "Het is vooral duidelijker. Je ziet direct de totale kosten voor een werkende installatie." },
      { question: "Kan ik meerdere kamers meenemen?", answer: "Ja, je kunt direct aangeven voor welke ruimtes je airco zoekt." },
    ],
    ctaVariant: "offerte",
    relatedSlugs: ["airco-kopen-en-laten-installeren-limburg", "airco-installatie-prijs-limburg", "airco-laten-installeren-kosten-limburg"],
    canonicalSlug: "airco-inclusief-montage-limburg",
    indexable: true,
  },
  {
    slug: "airco-installatie-prijs-limburg",
    title: "Airco installatie prijs Limburg",
    metaTitle: "Airco installatie prijs Limburg",
    metaDescription: "Airco installatie prijs in Limburg weten? Vraag vrijblijvend een duidelijke offerte aan voor jouw woning.",
    h1: "Airco installatie prijs in Limburg",
    intentType: "price",
    category: "price_offer",
    primaryKeyword: "Airco installatie prijs Limburg",
    secondaryKeywords: ["kosten airco installatie Limburg", "airco montage prijs", "prijs airco plaatsen Limburg"],
    intro: "Wil je weten wat airco installatie in Limburg kost? Vraag een vrijblijvende offerte aan en ontvang een prijsindicatie op basis van jouw woning, ruimte en wensen.",
    benefits: ["Snel prijsinzicht", "Duidelijke specificatie", "Vrijblijvend vergelijken"],
    whyTitle: "Waarom een prijs op maat aanvragen?",
    whyBody: "Online richtprijzen zijn handig, maar jouw woning bepaalt de echte installatieprijs. Leidinglengte, vermogen en montageplek maken veel verschil.",
    costTitle: "Welke factoren bepalen de installatieprijs?",
    costBody: "Belangrijke factoren zijn systeemtype, aantal binnenunits, montageplek buitenunit, leidingwerk, elektrische aansluiting en afwerking.",
    processTitle: "Hoe werkt een prijsaanvraag?",
    attentionTitle: "Waar moet je op letten bij prijzen?",
    attentionPoints: ["Vraag om totaalprijs inclusief btw.", "Controleer standaard leidinglengte.", "Let op merk, vermogen en garantie.", "Vergelijk offertes met dezelfde scope."],
    faq: [
      { question: "Kan ik direct een vaste prijs krijgen?", answer: "Soms bij standaard montage, maar een accurate prijs vraagt meestal om extra informatie." },
      { question: "Waarom verschillen installatieprijzen?", answer: "Door verschillen in toestel, montagewerk, leidinglengte, garantie en afwerking." },
      { question: "Is de goedkoopste offerte verstandig?", answer: "Niet altijd. Kijk ook naar kwaliteit, garantie en wat exact inbegrepen is." },
      { question: "Kost multi split meer?", answer: "Ja, meestal door extra binnenunits, leidingwerk en afstelling." },
    ],
    ctaVariant: "offerte",
    relatedSlugs: ["airco-laten-installeren-kosten-limburg", "airco-inclusief-montage-limburg", "airco-offerte-aanvragen-limburg"],
    canonicalSlug: "airco-installatie-prijs-limburg",
    indexable: true,
  },
  {
    slug: "airco-laten-installeren-kosten-limburg",
    title: "Airco laten installeren kosten Limburg",
    metaTitle: "Airco laten installeren kosten Limburg",
    metaDescription: "Kosten voor airco laten installeren in Limburg? Vraag gratis een duidelijke offerte aan voor jouw situatie.",
    h1: "Airco laten installeren kosten in Limburg",
    intentType: "price",
    category: "price_offer",
    primaryKeyword: "Airco laten installeren kosten Limburg",
    secondaryKeywords: ["airco installatie kosten Limburg", "kosten airco plaatsen", "airco offerte kosten"],
    intro: "De kosten voor een airco laten installeren in Limburg verschillen per woning. Vraag vrijblijvend een offerte aan en krijg duidelijkheid over toestel, montage en eventuele extra werkzaamheden.",
    benefits: ["Kosten op maat", "Geen verrassingen", "Persoonlijk advies"],
    whyTitle: "Waarom kosten vooraf laten berekenen?",
    whyBody: "Met een goede kosteninschatting weet je of single split, multi split of een andere oplossing past bij je budget en woning.",
    costTitle: "Welke kostenposten zijn er?",
    costBody: "Denk aan toestel, binnenunit, buitenunit, koelleidingen, condensafvoer, elektra, bevestiging, arbeid en afwerking.",
    processTitle: "Hoe werkt het aanvragen van kosteninformatie?",
    attentionTitle: "Waar moet je op letten bij kosten?",
    attentionPoints: ["Vraag of btw is inbegrepen.", "Controleer materiaal- en arbeidskosten.", "Let op meerwerk voor lange leidingen.", "Vraag naar onderhoudskosten na installatie."],
    faq: [
      { question: "Wat is het verschil tussen prijs en kosten?", answer: "Prijs is vaak het aanbod, kosten omvatten alle onderdelen die nodig zijn voor een werkende installatie." },
      { question: "Zijn onderhoudskosten verplicht?", answer: "Niet altijd verplicht, maar onderhoud helpt rendement en levensduur behouden." },
      { question: "Kan ik besparen op installatie?", answer: "Ja, door een slimme plaatsing en passend systeem te kiezen, niet door noodzakelijke montage over te slaan." },
      { question: "Is een offerte echt vrijblijvend?", answer: "Ja, je kunt de offerte beoordelen zonder verplichting." },
    ],
    ctaVariant: "offerte",
    relatedSlugs: ["airco-installatie-prijs-limburg", "airco-inclusief-montage-limburg", "single-split-airco-installatie-limburg"],
    canonicalSlug: "airco-laten-installeren-kosten-limburg",
    indexable: true,
  },
  {
    slug: "airco-plaatsen-binnen-1-week-limburg",
    title: "Airco plaatsen binnen 1 week Limburg",
    metaTitle: "Airco binnen 1 week plaatsen Limburg",
    metaDescription: "Airco snel laten plaatsen in Limburg? Vraag beschikbaarheid aan en ontvang persoonlijk advies.",
    h1: "Airco plaatsen binnen 1 week in Limburg",
    intentType: "transactional",
    category: "price_offer",
    primaryKeyword: "Airco plaatsen binnen 1 week Limburg",
    secondaryKeywords: ["airco snel plaatsen Limburg", "airco korte termijn", "snelle airco installatie Limburg"],
    intro: "Wil je snel een airco laten plaatsen in Limburg? Vraag direct beschikbaarheid aan. Je ontvangt snel reactie of plaatsing binnen korte termijn mogelijk is.",
    benefits: ["Snelle reactie", "Beschikbaarheid checken", "Vrijblijvende aanvraag"],
    whyTitle: "Waarom snel plaatsen aanvragen?",
    whyBody: "Tijdens warme periodes lopen agenda's snel vol. Door direct je situatie door te geven, kan sneller worden beoordeeld of korte termijn plaatsing haalbaar is.",
    costTitle: "Kost snelle plaatsing extra?",
    costBody: "Dat verschilt per planning en situatie. Een standaard installatie met goede bereikbaarheid is makkelijker snel in te plannen dan complex maatwerk.",
    processTitle: "Hoe werkt een snelle plaatsingsaanvraag?",
    attentionTitle: "Waar moet je op letten bij snelle plaatsing?",
    attentionPoints: ["Geef duidelijke foto's en ruimteinformatie door.", "Wees flexibel in planning.", "Vraag wat direct leverbaar is.", "Controleer of spoed geen onduidelijke meerkosten geeft."],
    faq: [
      { question: "Is plaatsing binnen 1 week altijd mogelijk?", answer: "Nee, dat hangt af van planning, voorraad en montagecomplexiteit." },
      { question: "Welke installaties kunnen het snelst?", answer: "Standaard single split installaties zijn meestal het snelst te beoordelen en te plannen." },
      { question: "Moet ik vooraf foto's sturen?", answer: "Dat helpt om sneller een inschatting te maken van plaatsing en leidingwerk." },
      { question: "Kan ik ook advies krijgen bij spoed?", answer: "Ja, ook bij snelle plaatsing blijft een passend vermogen belangrijk." },
    ],
    ctaVariant: "spoed",
    relatedSlugs: ["airco-spoed-installatie-limburg", "single-split-airco-installatie-limburg", "airco-inclusief-montage-limburg"],
    canonicalSlug: "airco-plaatsen-binnen-1-week-limburg",
    indexable: true,
  },
  {
    slug: "airco-spoed-installatie-limburg",
    title: "Airco spoed installatie Limburg",
    metaTitle: "Airco spoed installatie Limburg",
    metaDescription: "Airco spoed installatie in Limburg nodig? Vraag direct beschikbaarheid aan en ontvang snel reactie.",
    h1: "Airco spoed installatie in Limburg",
    intentType: "transactional",
    category: "price_offer",
    primaryKeyword: "Airco spoed installatie Limburg",
    secondaryKeywords: ["spoed airco plaatsen", "airco snel installeren Limburg", "airco montage spoed"],
    intro: "Heb je met spoed een airco-installatie nodig in Limburg? Dien je aanvraag in en laat snel beoordelen welke oplossing en planning mogelijk zijn.",
    benefits: ["Spoedaanvraag", "Snel duidelijkheid", "Beschikbaarheid check"],
    whyTitle: "Wanneer is spoed installatie zinvol?",
    whyBody: "Spoed is vooral relevant bij hitte, kwetsbare bewoners, thuiswerken of een ruimte die direct bruikbaar moet worden. Snelheid mag niet ten koste gaan van juiste montage.",
    costTitle: "Wat kost spoed installatie in Limburg?",
    costBody: "Spoedkosten hangen af van beschikbaarheid, toestelkeuze en montage. Vraag altijd om duidelijke prijsafspraken voor akkoord.",
    processTitle: "Hoe werkt een spoedaanvraag?",
    attentionTitle: "Waar moet je op letten bij spoed?",
    attentionPoints: ["Vraag direct naar beschikbaarheid.", "Lever zoveel mogelijk informatie aan.", "Controleer of het toestel op voorraad is.", "Laat de prijs vooraf bevestigen."],
    faq: [
      { question: "Kan een airco morgen al geplaatst worden?", answer: "Soms, maar dit hangt af van agenda, voorraad en montagecomplexiteit." },
      { question: "Is spoed alleen in de zomer mogelijk?", answer: "Nee, spoed kan ook nodig zijn voor verwarmen, storingen of specifieke ruimtes." },
      { question: "Welke gegevens zijn nodig?", answer: "Ruimte, woningtype, gewenste plek, foto's en contactgegevens helpen bij snelle beoordeling." },
      { question: "Is spoed duurder?", answer: "Dat kan, afhankelijk van planning. Vraag daarom om een duidelijke offerte." },
    ],
    ctaVariant: "spoed",
    relatedSlugs: ["airco-plaatsen-binnen-1-week-limburg", "airco-installatie-limburg", "airco-offerte-aanvragen-limburg"],
    canonicalSlug: "airco-spoed-installatie-limburg",
    indexable: true,
  },
  {
    slug: "airco-onderhoud-limburg",
    title: "Airco onderhoud Limburg",
    metaTitle: "Airco onderhoud Limburg | Basis en Premium",
    metaDescription: "Onderhoud voor bestaande StayCool-klanten in Limburg. Basis €13 of Premium €16 per maand per complete unit. Vergelijk de pakketten.",
    h1: "Airco onderhoud in Limburg",
    intentType: "service",
    category: "maintenance",
    primaryKeyword: "Airco onderhoud Limburg",
    secondaryKeywords: ["airco service Limburg", "airco onderhoudsbeurt", "airco laten controleren"],
    intro: "Houd uw airco in goede conditie met jaarlijks onderhoud. Voor airco’s die StayCool heeft geleverd en geïnstalleerd kiest u Basis of Premium, of een losse onderhoudsbeurt.",
    benefits: ["Minder kans op storingen", "Betere luchtkwaliteit", "Basis of Premium kiezen"],
    whyTitle: "Waarom airco onderhoud laten doen?",
    whyBody: "Filters, warmtewisselaars en afvoer vervuilen door gebruik. Onderhoud helpt je airco zuinig, veilig en fris te laten werken.",
    costTitle: "Wat kost airco onderhoud in Limburg?",
    costBody: "Een losse beurt kost €189 per complete unit. Basis kost €13 per maand en Premium €16 per maand per complete unit: één buitendeel met één binnendeel. Een extra binnendeel kost €8 bij Basis of €11 bij Premium per maand.",
    processTitle: "Hoe werkt een onderhoudsaanvraag?",
    attentionTitle: "Waar moet je op letten bij onderhoud?",
    attentionPoints: ["Laat filters en wisselaars reinigen.", "Controleer condensafvoer.", "Vraag naar onderhoudsrapportage.", "Plan onderhoud voor het warme seizoen."],
    faq: [
      { question: "Hoe vaak heeft een airco onderhoud nodig?", answer: "Voor woningen is jaarlijks onderhoud vaak verstandig, afhankelijk van gebruik." },
      { question: "Kan ik filters zelf reinigen?", answer: "Ja, basisfilterreiniging kan vaak zelf. Technische controle laat je beter uitvoeren." },
      { question: "Helpt onderhoud tegen geur?", answer: "Ja, vervuiling kan geur veroorzaken. Reiniging helpt dit voorkomen." },
      { question: "Is onderhoud verplicht?", answer: "Niet altijd voor kleine systemen, maar het is wel belangrijk voor garantie, rendement en levensduur." },
    ],
    ctaVariant: "onderhoud",
    relatedSlugs: ["airco-reinigen-limburg", "airco-bijvullen-limburg", "airco-storing-oplossen-limburg"],
    canonicalSlug: "airco-onderhoud-limburg",
    indexable: true,
  },
  {
    slug: "airco-reinigen-limburg",
    title: "Airco reinigen Limburg",
    metaTitle: "Airco reinigen Limburg | Service",
    metaDescription: "Airco laten reinigen in Limburg? Vraag vrijblijvend service aan voor frisse lucht en betere werking.",
    h1: "Airco reinigen in Limburg",
    intentType: "service",
    category: "maintenance",
    primaryKeyword: "Airco reinigen Limburg",
    secondaryKeywords: ["airco schoonmaken Limburg", "airco filter reinigen", "airco geur verwijderen"],
    intro: "Ruikt je airco muf of werkt hij minder fris? Vraag vrijblijvend airco reiniging in Limburg aan voor betere luchtkwaliteit en een schonere binnenunit.",
    benefits: ["Frissere lucht", "Minder geurklachten", "Snel service aanvragen"],
    whyTitle: "Waarom je airco laten reinigen?",
    whyBody: "Vuil, stof en vocht kunnen zich ophopen in de binnenunit. Reiniging helpt geuren, verminderde werking en vervuilde luchtstroom te voorkomen.",
    costTitle: "Wat kost airco reinigen in Limburg?",
    costBody: "De prijs hangt af van het aantal binnenunits en de mate van vervuiling. Combineer reiniging eventueel met een onderhoudscontrole.",
    processTitle: "Hoe werkt een reinigingsaanvraag?",
    attentionTitle: "Waar moet je op letten bij reinigen?",
    attentionPoints: ["Laat niet alleen filters, maar ook de binnenunit controleren.", "Gebruik geen agressieve middelen zelf.", "Combineer reiniging met condensafvoercontrole.", "Plan reiniging bij geur of zichtbaar vuil."],
    faq: [
      { question: "Wanneer moet ik mijn airco laten reinigen?", answer: "Bij muffe geur, stof, slechtere luchtstroom of als onderhoud lang geleden is." },
      { question: "Is filter reinigen genoeg?", answer: "Niet altijd. Vervuiling kan dieper in de binnenunit zitten." },
      { question: "Helpt reinigen tegen allergieklachten?", answer: "Schone filters en units kunnen de luchtkwaliteit verbeteren, maar lossen niet elke klacht op." },
      { question: "Kan reinigen samen met onderhoud?", answer: "Ja, dat is vaak de meest logische combinatie." },
    ],
    ctaVariant: "onderhoud",
    relatedSlugs: ["airco-onderhoud-limburg", "airco-storing-oplossen-limburg", "airco-reparatie-limburg"],
    canonicalSlug: "airco-reinigen-limburg",
    indexable: true,
  },
  {
    slug: "airco-bijvullen-limburg",
    title: "Airco bijvullen Limburg",
    metaTitle: "Airco bijvullen Limburg | Service",
    metaDescription: "Airco bijvullen in Limburg? Laat eerst controleren of er lekkage of een technisch probleem is.",
    h1: "Airco bijvullen in Limburg",
    intentType: "service",
    category: "maintenance",
    primaryKeyword: "Airco bijvullen Limburg",
    secondaryKeywords: ["airco koudemiddel bijvullen", "airco koelt niet goed", "airco lekcontrole Limburg"],
    intro: "Koelt je airco minder goed en denk je aan bijvullen? Vraag service aan in Limburg. Een controle is belangrijk, want koudemiddelverlies kan wijzen op lekkage.",
    benefits: ["Technische controle", "Duidelijke diagnose", "Vrijblijvend serviceverzoek"],
    whyTitle: "Waarom airco bijvullen laten controleren?",
    whyBody: "Een gesloten aircosysteem hoort normaal weinig koudemiddel te verliezen. Bijvullen zonder diagnose lost de oorzaak mogelijk niet op.",
    costTitle: "Wat kost airco bijvullen in Limburg?",
    costBody: "Kosten hangen af van diagnose, koudemiddeltype, hoeveelheid en eventuele reparatie. Vraag om heldere uitleg voordat er wordt bijgevuld.",
    processTitle: "Hoe werkt een bijvulaanvraag?",
    attentionTitle: "Waar moet je op letten bij bijvullen?",
    attentionPoints: ["Laat eerst lekkage uitsluiten.", "Vraag welk koudemiddel wordt gebruikt.", "Controleer of de monteur gecertificeerd is.", "Laat oorzaak en oplossing uitleggen."],
    faq: [
      { question: "Moet een airco regelmatig worden bijgevuld?", answer: "Nee, normaal niet. Koudemiddelverlies wijst vaak op een probleem." },
      { question: "Waarom koelt mijn airco minder?", answer: "Dat kan door vervuiling, verkeerde instelling, lekkage of technisch defect komen." },
      { question: "Mag iedereen koudemiddel bijvullen?", answer: "Nee, hiervoor gelden certificering en regels." },
      { question: "Is bijvullen altijd de oplossing?", answer: "Nee, de oorzaak moet eerst duidelijk zijn." },
    ],
    ctaVariant: "onderhoud",
    relatedSlugs: ["airco-storing-oplossen-limburg", "airco-reparatie-limburg", "airco-onderhoud-limburg"],
    canonicalSlug: "airco-bijvullen-limburg",
    indexable: true,
  },
  {
    slug: "airco-storing-oplossen-limburg",
    title: "Airco storing oplossen Limburg",
    metaTitle: "Airco storing oplossen Limburg",
    metaDescription: "Airco storing in Limburg? Vraag snel service aan voor diagnose, reparatieadvies en duidelijke vervolgstappen.",
    h1: "Airco storing oplossen in Limburg",
    intentType: "service",
    category: "maintenance",
    primaryKeyword: "Airco storing oplossen Limburg",
    secondaryKeywords: ["airco storing Limburg", "airco doet het niet", "airco foutcode Limburg"],
    intro: "Geeft je airco een storing of koelt hij niet meer goed? Vraag service aan in Limburg en krijg snel duidelijkheid over diagnose, reparatie en kosten.",
    benefits: ["Snel diagnose aanvragen", "Duidelijke vervolgstap", "Service in Limburg"],
    whyTitle: "Waarom een storing snel laten oplossen?",
    whyBody: "Door storingen tijdig te laten beoordelen voorkom je vervolgschade, onnodig energieverbruik en comfortproblemen tijdens warme of koude periodes.",
    costTitle: "Wat kost een airco storing oplossen?",
    costBody: "De kosten hangen af van diagnose, onderdelen, arbeidsduur en eventuele voorrijkosten. Vraag vooraf naar de werkwijze.",
    processTitle: "Hoe werkt een storingsaanvraag?",
    attentionTitle: "Waar moet je op letten bij storing?",
    attentionPoints: ["Noteer foutcode of symptomen.", "Controleer filters en afstandsbediening.", "Schakel bij lekkage of vreemde geluiden service in.", "Vraag om diagnose voor reparatie."],
    faq: [
      { question: "Wat moet ik doen bij een foutcode?", answer: "Noteer de code en geef die mee in je aanvraag. Dat versnelt de diagnose." },
      { question: "Kan ik zelf iets controleren?", answer: "Controleer filters, stroom, instellingen en batterijen van de afstandsbediening." },
      { question: "Wanneer is spoed nodig?", answer: "Bij lekkage, brandlucht, harde geluiden of uitval in kritieke ruimtes." },
      { question: "Kan elke storing direct worden opgelost?", answer: "Niet altijd. Soms zijn onderdelen of vervolgafspraken nodig." },
    ],
    ctaVariant: "onderhoud",
    relatedSlugs: ["airco-reparatie-limburg", "airco-bijvullen-limburg", "airco-onderhoud-limburg"],
    canonicalSlug: "airco-storing-oplossen-limburg",
    indexable: true,
  },
  {
    slug: "airco-reparatie-limburg",
    title: "Airco reparatie Limburg",
    metaTitle: "Airco reparatie Limburg | Service",
    metaDescription: "Airco reparatie in Limburg nodig? Vraag vrijblijvend service aan voor diagnose en hersteladvies.",
    h1: "Airco reparatie in Limburg",
    intentType: "service",
    category: "maintenance",
    primaryKeyword: "Airco reparatie Limburg",
    secondaryKeywords: ["airco herstellen Limburg", "airco monteur reparatie", "airco kapot Limburg"],
    intro: "Is je airco kapot of werkt hij niet zoals verwacht? Vraag vrijblijvend airco reparatie in Limburg aan en ontvang duidelijkheid over diagnose, kosten en planning.",
    benefits: ["Diagnose aanvragen", "Duidelijke reparatiekosten", "Persoonlijk advies"],
    whyTitle: "Waarom airco reparatie laten uitvoeren?",
    whyBody: "Een defecte airco kan meer energie verbruiken of verder beschadigen. Met een diagnose weet je of reparatie zinvol is of vervanging beter past.",
    costTitle: "Wat kost airco reparatie in Limburg?",
    costBody: "De kosten hangen af van storing, onderdelen en arbeidsduur. Een duidelijke diagnose voorkomt dat je onnodig reparaties laat uitvoeren.",
    processTitle: "Hoe werkt een reparatieaanvraag?",
    attentionTitle: "Waar moet je op letten bij reparatie?",
    attentionPoints: ["Beschrijf symptomen concreet.", "Vraag om diagnose voor akkoord.", "Controleer garantie op onderdelen.", "Vergelijk reparatiekosten met vervanging bij oude systemen."],
    faq: [
      { question: "Wanneer is reparatie nog zinvol?", answer: "Bij relatief jonge systemen of beperkte defecten is reparatie vaak logisch." },
      { question: "Wanneer kan vervangen beter zijn?", answer: "Bij oude systemen, dure onderdelen of laag rendement kan vervanging verstandiger zijn." },
      { question: "Kan reparatie direct plaatsvinden?", answer: "Soms wel, maar bij specifieke onderdelen kan een vervolgafspraak nodig zijn." },
      { question: "Moet ik merk en type doorgeven?", answer: "Ja, dat helpt bij snelle diagnose en onderdelencontrole." },
    ],
    ctaVariant: "onderhoud",
    relatedSlugs: ["airco-storing-oplossen-limburg", "airco-bijvullen-limburg", "airco-onderhoud-limburg"],
    canonicalSlug: "airco-reparatie-limburg",
    indexable: true,
  },
  {
    slug: "airco-voor-appartement-limburg",
    title: "Airco voor appartement Limburg",
    metaTitle: "Airco voor appartement Limburg",
    metaDescription: "Airco voor een appartement in Limburg? Vraag advies over mogelijkheden, buitenunit en VvE-regels.",
    h1: "Airco voor appartement in Limburg",
    intentType: "use_case",
    category: "room",
    primaryKeyword: "Airco voor appartement Limburg",
    secondaryKeywords: ["appartement airco Limburg", "airco VvE Limburg", "airco balkon buitenunit"],
    intro: "Een airco in een appartement vraagt extra aandacht voor buitenunit, geluid, balkon en VvE-regels. Vraag vrijblijvend advies en een offerte aan in Limburg.",
    benefits: ["Advies voor appartement", "Check buitenunitopties", "Vrijblijvende aanvraag"],
    whyTitle: "Waarom appartement-specifiek advies?",
    whyBody: "Bij appartementen spelen buren, gevel, balkon en regels vaak een grotere rol. De juiste oplossing voorkomt discussie en praktische problemen.",
    costTitle: "Wat kost een appartement airco in Limburg?",
    costBody: "De kosten hangen af van unitpositie, leidingroute, hoogte, bereikbaarheid en toestemming. Een opname is vaak belangrijk.",
    processTitle: "Hoe werkt de aanvraag voor appartement?",
    attentionTitle: "Waar moet je op letten in een appartement?",
    attentionPoints: ["Controleer VvE-regels of verhuurderseisen.", "Bespreek geluid naar buren.", "Kijk naar balkon- of gevelplaatsing.", "Vraag om nette leidingafwerking."],
    faq: [
      { question: "Mag ik zomaar een buitenunit plaatsen?", answer: "Niet altijd. Controleer VvE, verhuurder en lokale regels." },
      { question: "Kan een airco zonder buitenunit in een appartement?", answer: "Soms is dat een alternatief, maar prestaties en geluid verschillen van split airco." },
      { question: "Is balkonplaatsing mogelijk?", answer: "Vaak wel, mits veilig, bereikbaar en toegestaan." },
      { question: "Welke ruimte wordt meestal gekoeld?", answer: "Vaak woonkamer of slaapkamer, afhankelijk van comfortwens." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-zonder-buitenunit-limburg", "airco-met-buitenunit-plaatsen-limburg", "single-split-airco-installatie-limburg"],
    canonicalSlug: "airco-voor-appartement-limburg",
    indexable: true,
  },
  {
    slug: "airco-voor-vrijstaand-huis-limburg",
    title: "Airco voor vrijstaand huis Limburg",
    metaTitle: "Airco vrijstaand huis Limburg",
    metaDescription: "Airco voor een vrijstaand huis in Limburg? Vraag vrijblijvend advies voor een of meerdere ruimtes.",
    h1: "Airco voor vrijstaand huis in Limburg",
    intentType: "use_case",
    category: "room",
    primaryKeyword: "Airco voor vrijstaand huis Limburg",
    secondaryKeywords: ["airco vrijstaande woning", "multi split vrijstaand huis", "airco woning Limburg"],
    intro: "Voor een vrijstaand huis is vaak meer mogelijk, maar ook meer keuze. Vraag een vrijblijvende offerte aan voor een passende airco-oplossing in Limburg.",
    benefits: ["Advies voor hele woning", "Single of multi split", "Duidelijke offerte"],
    whyTitle: "Waarom maatwerk voor een vrijstaand huis?",
    whyBody: "Vrijstaande woningen verschillen sterk in inhoud, isolatie en indeling. Soms is een systeem voor meerdere ruimtes logischer dan een losse unit.",
    costTitle: "Wat kost airco in een vrijstaand huis?",
    costBody: "De kosten hangen af van aantal ruimtes, systeemtype, leidingroutes en buitenunitpositie. Multi split of meerdere single split systemen zijn opties.",
    processTitle: "Hoe werkt de aanvraag voor vrijstaand huis?",
    attentionTitle: "Waar moet je op letten bij vrijstaande woningen?",
    attentionPoints: ["Bekijk of meerdere ruimtes gekoeld moeten worden.", "Stem vermogen af op grote inhoud.", "Kies logische buitenunitpositie.", "Denk aan toekomstige uitbreiding."],
    faq: [
      { question: "Is multi split geschikt voor een vrijstaand huis?", answer: "Vaak wel, vooral als meerdere kamers apart geregeld moeten worden." },
      { question: "Kan een airco een hele woning koelen?", answer: "Een enkele unit koelt meestal vooral de ruimte waar hij hangt. Voor hele woning is maatwerk nodig." },
      { question: "Waar komt de buitenunit?", answer: "Dat kan vaak aan gevel, op plat dak of op de grond, afhankelijk van situatie." },
      { question: "Is verwarmen met airco interessant?", answer: "Ja, vooral in goed geisoleerde ruimtes kan airco efficient bijverwarmen." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["multi-split-airco-installatie-limburg", "airco-met-verwarming-installeren-limburg", "beste-airco-voor-woning-limburg"],
    canonicalSlug: "airco-voor-vrijstaand-huis-limburg",
    indexable: true,
  },
  {
    slug: "airco-zonder-buitenunit-limburg",
    title: "Airco zonder buitenunit Limburg",
    metaTitle: "Airco zonder buitenunit Limburg",
    metaDescription: "Airco zonder buitenunit in Limburg? Vraag advies over mogelijkheden, beperkingen en alternatieven.",
    h1: "Airco zonder buitenunit in Limburg",
    intentType: "system_type",
    category: "system",
    primaryKeyword: "Airco zonder buitenunit Limburg",
    secondaryKeywords: ["monoblock airco Limburg", "airco zonder buitenunit appartement", "airco zonder buitenunit plaatsen"],
    intro: "Wil je een airco zonder buitenunit in Limburg, bijvoorbeeld door VvE-regels of beperkte buitenruimte? Vraag advies over geschikte oplossingen en alternatieven.",
    benefits: ["Geen losse buitenunit", "Geschikt bij beperkingen", "Duidelijk advies"],
    whyTitle: "Waarom kiezen voor zonder buitenunit?",
    whyBody: "Een oplossing zonder buitenunit kan uitkomst bieden als gevelplaatsing niet mag. Wel zijn geluid, rendement en montage anders dan bij split airco.",
    costTitle: "Wat kost een airco zonder buitenunit?",
    costBody: "De kosten hangen af van type monoblock, benodigde muurdoorvoeren en afwerking. Vergelijk altijd prestaties met split airco.",
    processTitle: "Hoe werkt de aanvraag voor zonder buitenunit?",
    attentionTitle: "Waar moet je op letten zonder buitenunit?",
    attentionPoints: ["Controleer geluidsniveau binnen.", "Vraag naar rendement en capaciteit.", "Bespreek muurdoorvoeren.", "Vergelijk met split airco als dat mag."],
    faq: [
      { question: "Werkt een airco zonder buitenunit goed?", answer: "Dat kan, maar prestaties en geluid verschillen van een split systeem." },
      { question: "Zijn muurdoorvoeren nodig?", answer: "Vaak wel, voor luchttoevoer en afvoer." },
      { question: "Is dit geschikt voor appartementen?", answer: "Soms juist wel, vooral als een buitenunit niet is toegestaan." },
      { question: "Is het goedkoper dan split airco?", answer: "Niet altijd. Vergelijk totaalprijs, comfort en rendement." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-voor-appartement-limburg", "airco-met-buitenunit-plaatsen-limburg", "welke-airco-heb-ik-nodig-limburg"],
    canonicalSlug: "airco-zonder-buitenunit-limburg",
    indexable: true,
  },
  {
    slug: "airco-met-verwarming-installeren-limburg",
    title: "Airco met verwarming installeren Limburg",
    metaTitle: "Airco met verwarming Limburg",
    metaDescription: "Airco met verwarming installeren in Limburg? Vraag advies over koelen, verwarmen en rendement.",
    h1: "Airco met verwarming installeren in Limburg",
    intentType: "use_case",
    category: "system",
    primaryKeyword: "Airco met verwarming installeren Limburg",
    secondaryKeywords: ["verwarmen met airco Limburg", "airco als verwarming", "airco warmtepomp Limburg"],
    intro: "Wil je koelen en verwarmen met een airco in Limburg? Vraag vrijblijvend advies over geschikte systemen, rendement en montage voor jouw woning.",
    benefits: ["Koelen en verwarmen", "Advies over rendement", "Passende offerte"],
    whyTitle: "Waarom airco met verwarming?",
    whyBody: "Een moderne airco is een lucht-lucht warmtepomp. Daarmee kun je in veel situaties efficient bijverwarmen en het gasverbruik verlagen.",
    costTitle: "Wat kost airco met verwarming in Limburg?",
    costBody: "De kosten lijken op een split airco installatie, maar systeemkeuze en rendement zijn extra belangrijk als je vaak wilt verwarmen.",
    processTitle: "Hoe werkt de aanvraag voor verwarmen met airco?",
    attentionTitle: "Waar moet je op letten bij verwarmen?",
    attentionPoints: ["Let op SCOP en rendement.", "Kies voldoende vermogen voor koude dagen.", "Bekijk isolatie van de ruimte.", "Bespreek gebruik naast bestaande verwarming."],
    faq: [
      { question: "Kan een airco echt verwarmen?", answer: "Ja, moderne split airco's kunnen efficient warmte leveren." },
      { question: "Vervangt dit mijn cv-ketel?", answer: "Meestal is het bijverwarming per ruimte, geen volledige cv-vervanging zonder plan." },
      { question: "Is verwarmen met airco goedkoper?", answer: "Dat hangt af van stroomprijs, gasprijs, rendement en isolatie." },
      { question: "Welke ruimtes zijn geschikt?", answer: "Woonkamers, slaapkamers, zolders en thuiskantoren zijn vaak geschikt." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["energiezuinige-airco-laten-plaatsen-limburg", "airco-voor-vrijstaand-huis-limburg", "beste-airco-voor-woning-limburg"],
    canonicalSlug: "airco-met-verwarming-installeren-limburg",
    indexable: true,
  },
  {
    slug: "energiezuinige-airco-laten-plaatsen-limburg",
    title: "Energiezuinige airco laten plaatsen Limburg",
    metaTitle: "Energiezuinige airco plaatsen Limburg",
    metaDescription: "Energiezuinige airco laten plaatsen in Limburg? Vraag advies over rendement, verbruik en montage.",
    h1: "Energiezuinige airco laten plaatsen in Limburg",
    intentType: "use_case",
    category: "system",
    primaryKeyword: "Energiezuinige airco laten plaatsen Limburg",
    secondaryKeywords: ["zuinige airco Limburg", "A+++ airco plaatsen", "airco laag verbruik"],
    intro: "Zoek je een energiezuinige airco in Limburg? Vraag vrijblijvend advies aan over rendement, capaciteit en installatie voor jouw woning.",
    benefits: ["Lager verbruik", "Advies over rendement", "Vrijblijvende offerte"],
    whyTitle: "Waarom kiezen voor energiezuinig?",
    whyBody: "Een energiezuinige airco verlaagt verbruik bij koelen en verwarmen. Het juiste vermogen is net zo belangrijk als het energielabel.",
    costTitle: "Wat kost een energiezuinige airco?",
    costBody: "Zuinige modellen zijn soms duurder in aanschaf, maar kunnen lagere gebruikskosten geven. Een offerte maakt de balans helder.",
    processTitle: "Hoe werkt de aanvraag voor een zuinige airco?",
    attentionTitle: "Waar moet je op letten bij energiezuinigheid?",
    attentionPoints: ["Let op SEER en SCOP waarden.", "Kies passend vermogen.", "Vergelijk gebruikskosten, niet alleen aanschafprijs.", "Vraag naar slimme bediening en standen."],
    faq: [
      { question: "Wat betekent A+++ bij airco?", answer: "Het geeft de energieprestatie aan, maar vermogen en gebruik bepalen ook je kosten." },
      { question: "Is een duurdere airco zuiniger?", answer: "Vaak wel, maar niet altijd. Vergelijk rendement en toepassing." },
      { question: "Kan een zuinige airco verwarmen?", answer: "Ja, veel energiezuinige modellen zijn geschikt voor koelen en verwarmen." },
      { question: "Helpt goede plaatsing bij verbruik?", answer: "Ja, juiste plaatsing en capaciteit voorkomen onnodig energiegebruik." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-met-verwarming-installeren-limburg", "airco-vermogen-berekenen-limburg", "beste-airco-voor-woning-limburg"],
    canonicalSlug: "energiezuinige-airco-laten-plaatsen-limburg",
    indexable: true,
  },
  {
    slug: "welke-airco-heb-ik-nodig-limburg",
    title: "Welke airco heb ik nodig Limburg",
    metaTitle: "Welke airco heb ik nodig Limburg",
    metaDescription: "Welke airco heb je nodig in Limburg? Vraag vrijblijvend advies voor de juiste capaciteit en oplossing.",
    h1: "Welke airco heb ik nodig in Limburg?",
    intentType: "advice",
    category: "advice",
    primaryKeyword: "Welke airco heb ik nodig Limburg",
    secondaryKeywords: ["airco advies Limburg", "airco keuzehulp", "juiste airco kiezen"],
    intro: "Twijfel je welke airco je nodig hebt in Limburg? Vraag vrijblijvend advies aan en krijg duidelijkheid over type systeem, vermogen en montage voor jouw woning.",
    benefits: ["Keuzehulp", "Advies op woningniveau", "Vrijblijvende offerte"],
    whyTitle: "Waarom advies aanvragen?",
    whyBody: "De juiste airco hangt af van ruimte, isolatie, zoninval, gebruik en budget. Advies voorkomt een te zwak, te luid of onpraktisch systeem.",
    costTitle: "Wat kost de juiste airco?",
    costBody: "De kosten volgen uit de gekozen oplossing. Eerst bepalen welk systeem past, daarna vergelijk je de offerte op prijs en inhoud.",
    processTitle: "Hoe werkt airco advies aanvragen?",
    attentionTitle: "Waar moet je op letten bij kiezen?",
    attentionPoints: ["Bepaal welke ruimtes belangrijk zijn.", "Laat vermogen berekenen.", "Vergelijk single split, multi split en monoblock.", "Let op geluid, rendement en plaatsing."],
    faq: [
      { question: "Hoe weet ik hoeveel kW ik nodig heb?", answer: "Dat hangt af van inhoud, isolatie en zonbelasting. Een capaciteitsberekening geeft richting." },
      { question: "Moet ik kiezen voor single of multi split?", answer: "Single split is voor een ruimte, multi split voor meerdere kamers." },
      { question: "Welke airco is het stilst?", answer: "Dat verschilt per model en stand. Vooral slaapkamers vragen om lage geluidswaarden." },
      { question: "Kan ik direct een offerte krijgen?", answer: "Ja, na je aanvraag kan een passende offerte of adviesstap volgen." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["beste-airco-voor-woning-limburg", "airco-vermogen-berekenen-limburg", "airco-vergelijken-limburg"],
    canonicalSlug: "welke-airco-heb-ik-nodig-limburg",
    indexable: true,
  },
  {
    slug: "beste-airco-voor-woning-limburg",
    title: "Beste airco voor woning Limburg",
    metaTitle: "Beste airco voor woning Limburg",
    metaDescription: "Beste airco voor jouw woning in Limburg vinden? Vraag vrijblijvend advies en vergelijk passende opties.",
    h1: "Beste airco voor woning in Limburg",
    intentType: "advice",
    category: "advice",
    primaryKeyword: "Beste airco voor woning Limburg",
    secondaryKeywords: ["beste airco woning", "airco advies woning Limburg", "airco kiezen woning"],
    intro: "De beste airco voor jouw woning in Limburg hangt af van ruimte, isolatie, budget en gebruik. Vraag vrijblijvend advies en ontvang passende opties.",
    benefits: ["Passend woningadvies", "Vergelijk systemen", "Duidelijke vervolgstap"],
    whyTitle: "Waarom bestaat er niet een beste airco?",
    whyBody: "Een airco die perfect is voor een slaapkamer kan te licht zijn voor een woonkamer. De beste keuze is afhankelijk van de situatie.",
    costTitle: "Wat kost de beste airco voor jouw woning?",
    costBody: "De prijs hangt af van capaciteit, merk, rendement en montage. Een passende offerte voorkomt dat je betaalt voor verkeerde specificaties.",
    processTitle: "Hoe werkt advies voor de beste airco?",
    attentionTitle: "Waar moet je op letten bij de beste keuze?",
    attentionPoints: ["Stem model af op ruimte en gebruik.", "Kijk naar geluid en rendement.", "Vergelijk garantie en service.", "Laat montagepraktijk meewegen."],
    faq: [
      { question: "Wat is het beste merk airco?", answer: "Dat hangt af van budget, wensen en toepassing. Bekende merken verschillen in prijs, opties en garantie." },
      { question: "Is A+++ altijd de beste keuze?", answer: "Niet automatisch. Het systeem moet ook goed passen bij de ruimte." },
      { question: "Welke airco past bij meerdere kamers?", answer: "Dan is multi split vaak interessant." },
      { question: "Kan ik hulp krijgen bij vergelijken?", answer: "Ja, via de aanvraag kun je gericht advies en een offerte ontvangen." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["welke-airco-heb-ik-nodig-limburg", "energiezuinige-airco-laten-plaatsen-limburg", "airco-vergelijken-limburg"],
    canonicalSlug: "beste-airco-voor-woning-limburg",
    indexable: true,
  },
  {
    slug: "airco-vermogen-berekenen-limburg",
    title: "Airco vermogen berekenen Limburg",
    metaTitle: "Airco vermogen berekenen Limburg",
    metaDescription: "Airco vermogen berekenen in Limburg? Vraag advies voor de juiste capaciteit per ruimte.",
    h1: "Airco vermogen berekenen in Limburg",
    intentType: "advice",
    category: "advice",
    primaryKeyword: "Airco vermogen berekenen Limburg",
    secondaryKeywords: ["airco kW berekenen", "capaciteit airco Limburg", "airco vermogen woning"],
    intro: "Het juiste airco vermogen voorkomt te weinig koeling, onnodig geluid en hoger verbruik. Vraag advies aan voor een passende capaciteitsberekening in Limburg.",
    benefits: ["Juiste capaciteit", "Voorkom miskoop", "Advies per ruimte"],
    whyTitle: "Waarom vermogen laten berekenen?",
    whyBody: "Te weinig vermogen geeft onvoldoende comfort. Te veel vermogen kan onrustig draaien en minder efficient zijn. Een berekening voorkomt beide problemen.",
    costTitle: "Heeft vermogen invloed op de prijs?",
    costBody: "Ja. Meer vermogen of meerdere units verhogen vaak de investering. Daarom is een correcte berekening belangrijk voor kosten en comfort.",
    processTitle: "Hoe werkt vermogen berekenen?",
    attentionTitle: "Waar moet je op letten bij vermogen?",
    attentionPoints: ["Gebruik inhoud in m3, niet alleen m2.", "Neem isolatie en zoninval mee.", "Let op ramen en dakligging.", "Bereken per ruimte apart."],
    faq: [
      { question: "Is m2 genoeg om vermogen te bepalen?", answer: "Nee, plafondhoogte, isolatie en zoninval zijn ook belangrijk." },
      { question: "Wat gebeurt er bij te weinig vermogen?", answer: "De airco haalt de gewenste temperatuur moeilijker en draait langer." },
      { question: "Wat gebeurt er bij te veel vermogen?", answer: "Het systeem kan vaker schakelen en minder comfortabel aanvoelen." },
      { question: "Kan ik vermogen online berekenen?", answer: "Online kan een indicatie geven, maar voor offerte is woninginformatie belangrijk." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["welke-airco-heb-ik-nodig-limburg", "airco-voor-woonkamer-installeren-limburg", "airco-voor-zolder-installeren-limburg"],
    canonicalSlug: "airco-vermogen-berekenen-limburg",
    indexable: true,
  },
  {
    slug: "airco-offerte-aanvragen-limburg",
    title: "Airco offerte aanvragen Limburg",
    metaTitle: "Airco offerte aanvragen Limburg",
    metaDescription: "Airco offerte aanvragen in Limburg? Ontvang vrijblijvend een duidelijke reactie binnen 24 uur.",
    h1: "Airco offerte aanvragen in Limburg",
    intentType: "transactional",
    category: "price_offer",
    primaryKeyword: "Airco offerte aanvragen Limburg",
    secondaryKeywords: ["airco offerte Limburg", "airco prijs aanvragen", "vrijblijvende airco offerte"],
    intro: "Wil je een airco offerte aanvragen in Limburg? Vul kort je situatie in en ontvang snel een duidelijke reactie voor installatie, onderhoud of advies.",
    benefits: ["Vrijblijvend", "Persoonlijk advies", "Duidelijke vervolgstap"],
    whyTitle: "Waarom een offerte aanvragen?",
    whyBody: "Een offerte geeft duidelijkheid over kosten, systeemkeuze, montage en planning. Daarmee kun je gericht beslissen zonder losse aannames.",
    costTitle: "Wat staat er in een goede airco offerte?",
    costBody: "Een goede offerte benoemt toestel, vermogen, montage, materialen, garantie, planning en eventuele meerkosten.",
    processTitle: "Hoe werkt offerte aanvragen?",
    attentionTitle: "Waar moet je op letten bij offertes?",
    attentionPoints: ["Controleer totaalprijs en btw.", "Let op exacte scope van montage.", "Vergelijk garantie en service.", "Vraag om uitleg bij onduidelijke posten."],
    faq: [
      { question: "Is een offerte aanvragen gratis?", answer: "Ja, je kunt vrijblijvend een aanvraag doen." },
      { question: "Hoe snel krijg ik reactie?", answer: "Meestal binnen 24 uur." },
      { question: "Moet ik direct beslissen?", answer: "Nee, je beoordeelt zelf of de offerte past." },
      { question: "Kan ik meerdere wensen doorgeven?", answer: "Ja, vermeld ruimtes, gebruik en eventuele voorkeuren." },
    ],
    ctaVariant: "offerte",
    relatedSlugs: ["airco-installatie-prijs-limburg", "airco-laten-plaatsen-limburg", "airco-vergelijken-limburg"],
    canonicalSlug: "airco-offerte-aanvragen-limburg",
    indexable: true,
  },
  {
    slug: "airco-vergelijken-limburg",
    title: "Airco vergelijken Limburg",
    metaTitle: "Airco vergelijken Limburg | Advies",
    metaDescription: "Airco vergelijken in Limburg? Vraag advies en vergelijk systemen op prijs, geluid, rendement en montage.",
    h1: "Airco vergelijken in Limburg",
    intentType: "advice",
    category: "advice",
    primaryKeyword: "Airco vergelijken Limburg",
    secondaryKeywords: ["airco systemen vergelijken", "split airco vergelijken", "airco offerte vergelijken"],
    intro: "Wil je airco's vergelijken in Limburg? Vraag advies aan en vergelijk systemen op comfort, prijs, geluid, energieverbruik en montagegeschiktheid.",
    benefits: ["Vergelijk helder", "Voorkom verkeerde keuze", "Vrijblijvend advies"],
    whyTitle: "Waarom airco's vergelijken?",
    whyBody: "Airco's verschillen in vermogen, geluid, rendement, functies en montage-eisen. Vergelijken helpt om niet alleen op prijs te kiezen.",
    costTitle: "Hoe vergelijk je kosten eerlijk?",
    costBody: "Vergelijk altijd totaalprijs inclusief montage, materialen, garantie en service. Alleen toestelprijzen zeggen weinig.",
    processTitle: "Hoe werkt airco vergelijken via aanvraag?",
    attentionTitle: "Waar moet je op letten bij vergelijken?",
    attentionPoints: ["Vergelijk SEER en SCOP.", "Let op geluidsniveau.", "Controleer garantie en service.", "Vergelijk montage-inhoud."],
    faq: [
      { question: "Welke airco is het goedkoopst?", answer: "Dat verschilt per situatie. Goedkoop in aanschaf is niet altijd goedkoop in gebruik." },
      { question: "Waarop moet ik vooral vergelijken?", answer: "Vermogen, geluid, rendement, garantie en montagekwaliteit." },
      { question: "Is merk belangrijk?", answer: "Ja, maar toepassing en montage zijn minstens zo belangrijk." },
      { question: "Kan ik offertes vergelijken?", answer: "Ja, let dan goed op of dezelfde werkzaamheden inbegrepen zijn." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["beste-airco-voor-woning-limburg", "welke-airco-heb-ik-nodig-limburg", "airco-offerte-aanvragen-limburg"],
    canonicalSlug: "airco-vergelijken-limburg",
    indexable: true,
  },
  {
    slug: "airco-specialist-limburg",
    title: "Airco specialist Limburg",
    metaTitle: "Airco specialist Limburg | Advies",
    metaDescription: "Airco specialist in Limburg nodig? Vraag vrijblijvend advies en ontvang een passende offerte.",
    h1: "Airco specialist in Limburg",
    intentType: "advice",
    category: "advice",
    primaryKeyword: "Airco specialist Limburg",
    secondaryKeywords: ["airco advies Limburg", "airco installateur specialist", "airco expert Limburg"],
    intro: "Zoek je een airco specialist in Limburg voor advies, installatie of service? Dien je aanvraag in en ontvang snel een passende vervolgstap.",
    benefits: ["Gericht advies", "Passende specialist", "Persoonlijk advies"],
    whyTitle: "Waarom een airco specialist inschakelen?",
    whyBody: "Een specialist kijkt naar capaciteit, plaatsing, geluid, rendement en gebruik. Dat voorkomt technische problemen en verkeerde keuzes.",
    costTitle: "Wat kost hulp van een airco specialist?",
    costBody: "De kosten hangen af van advies, installatie of service. Voor een offerte is duidelijkheid over jouw vraag nodig.",
    processTitle: "Hoe werkt specialist aanvragen?",
    attentionTitle: "Waar moet je op letten bij een specialist?",
    attentionPoints: ["Let op ervaring met jouw type woning.", "Vraag om duidelijke uitleg.", "Controleer garantie en service.", "Bespreek planning en bereikbaarheid."],
    faq: [
      { question: "Wanneer heb ik een specialist nodig?", answer: "Bij installatie, storing, meerdere ruimtes of twijfel over vermogen en plaatsing." },
      { question: "Kan ik alleen advies aanvragen?", answer: "Ja, je kunt ook starten met advies of een offerteverzoek." },
      { question: "Is specialist hetzelfde als installateur?", answer: "Vaak overlapt dit, maar specialist benadrukt advies en oplossing op maat." },
      { question: "Hoe snel krijg ik reactie?", answer: "Meestal binnen 24 uur na je aanvraag." },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-installatie-limburg", "welke-airco-heb-ik-nodig-limburg", "airco-offerte-aanvragen-limburg"],
    canonicalSlug: "airco-specialist-limburg",
    indexable: true,
  },
  {
    slug: "airco-buitenunit-plaatsen-regels-limburg",
    title: "Airco buitenunit plaatsen regels Limburg",
    metaTitle: "Airco buitenunit regels Limburg",
    metaDescription: "Airco buitenunit plaatsen in Limburg? Vraag advies over regels, geluid, erfgrens en geschikte montageplek.",
    h1: "Airco buitenunit plaatsen volgens de regels in Limburg",
    intentType: "rules",
    category: "rules",
    primaryKeyword: "Airco buitenunit plaatsen regels Limburg",
    secondaryKeywords: ["airco buitenunit regels", "airco buitenunit plaatsen Limburg", "regels buitenunit airco"],
    intro: "Wil je een airco buitenunit plaatsen in Limburg, maar twijfel je over regels, geluid of buren? Vraag vrijblijvend advies aan voordat je een systeem kiest of laat monteren.",
    benefits: ["Voorkom gedoe met buren", "Duidelijke plaatsingscheck", "Vrijblijvend advies"],
    whyTitle: "Waarom regels vooraf checken?",
    whyBody: "Een buitenunit moet technisch goed staan en mag geen onnodige geluidsoverlast veroorzaken. Vooraf kijken naar erfgrens, gevel, dak, balkon en trillingen voorkomt herstelwerk achteraf.",
    costTitle: "Wat kost een buitenunit goed laten plaatsen?",
    costBody: "De kosten hangen af van montageplek, beugel of dakopstelling, leidinglengte, bereikbaarheid en geluidsmaatregelen. Een offerte maakt duidelijk welke oplossing haalbaar is.",
    processTitle: "Hoe werkt advies over buitenunit plaatsing?",
    attentionTitle: "Waar moet je op letten?",
    attentionPoints: ["Controleer geluid richting buren.", "Bespreek afstand tot erfgrens en ramen.", "Let op trillingsdemping en stevige montage.", "Laat bereikbaarheid voor onderhoud meewegen."],
    faq: [
      { question: "Mag een airco buitenunit overal staan?", answer: "Nee, de plek moet technisch geschikt zijn en mag geen onnodige hinder veroorzaken. Soms gelden gebouw-, VvE- of gemeenteregels." },
      { question: "Hoeveel geluid mag een buitenunit maken?", answer: "Voor buitenunits gelden geluidsnormen bij buren. Laat daarom vooraf beoordelen of de gekozen plek logisch is." },
      { question: "Is een dakopstelling toegestaan?", answer: "Dat kan, maar constructie, bereikbaarheid, trillingen en geluid moeten goed worden bekeken." },
      { question: "Kan ik eerst alleen advies krijgen?", answer: "Ja, juist bij twijfel over buitenunitplaatsing is een vrijblijvende check verstandig." },
    ],
    sourceLinks: [
      { label: "Omgevingsdienst Twente: warmtepompen, airco en geluid", url: "https://www.odtwente.nl/themas/geluid/warmtepompen%2Bairco%2Ben%2Bgeluid/default.aspx" },
      { label: "Omgevingsloket: vergunningcheck", url: "https://www.omgevingsloket.nl/" },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-met-buitenunit-plaatsen-limburg", "airco-buitenunit-erfgrens-limburg", "airco-geluidsoverlast-buren-limburg"],
    canonicalSlug: "airco-buitenunit-plaatsen-regels-limburg",
    indexable: true,
  },
  {
    slug: "airco-buitenunit-erfgrens-limburg",
    title: "Airco buitenunit erfgrens Limburg",
    metaTitle: "Airco buitenunit erfgrens Limburg",
    metaDescription: "Twijfel over airco buitenunit bij de erfgrens in Limburg? Vraag advies over geluid, afstand en plaatsing.",
    h1: "Airco buitenunit bij de erfgrens in Limburg",
    intentType: "rules",
    category: "rules",
    primaryKeyword: "Airco buitenunit erfgrens Limburg",
    secondaryKeywords: ["afstand airco buitenunit erfgrens", "airco erfgrens buren", "buitenunit dicht bij erfgrens"],
    intro: "Een airco buitenunit dicht bij de erfgrens kan vragen oproepen over geluid, trillingen en buren. Vraag advies aan voor een praktische plaatsingskeuze in Limburg.",
    benefits: ["Minder risico op klachten", "Plaatsing vooraf beoordelen", "Persoonlijk advies"],
    whyTitle: "Waarom erfgrens belangrijk is",
    whyBody: "De afstand tot de erfgrens is niet het enige punt. Het gaat vooral om geluid op de plek waar buren hinder kunnen ervaren, plus trillingen en reflectie tegen muren.",
    costTitle: "Wat kost plaatsing verder van de erfgrens?",
    costBody: "Een andere positie kan extra leidingwerk, beugels of dakopstelling vragen. De offerte laat zien of dat nodig is en wat het verschil in kosten is.",
    processTitle: "Hoe werkt een erfgrens-check?",
    attentionTitle: "Waar moet je op letten?",
    attentionPoints: ["Kijk naar ramen en terrassen van buren.", "Voorkom montage tegen galmende vlakken.", "Gebruik trillingsdempers bij wand- of dakmontage.", "Leg afspraken met VvE of buren duidelijk vast."],
    faq: [
      { question: "Is er een vaste afstand tot de erfgrens?", answer: "Niet elke situatie is hetzelfde. Geluid, omgeving en montageplek bepalen of een positie verstandig is." },
      { question: "Kan een buitenunit tegen een schutting?", answer: "Dat is vaak ongunstig voor luchtstroming en geluid. Laat dit vooraf beoordelen." },
      { question: "Wat als buren bezwaar maken?", answer: "Dan helpt een onderbouwde plaatsingskeuze met aandacht voor geluid en trillingen." },
      { question: "Kan de installateur meedenken over de plek?", answer: "Ja, geef bij je aanvraag aan dat erfgrens en buren belangrijk zijn." },
    ],
    sourceLinks: [
      { label: "Omgevingsdienst Twente: geluid bij erfgrens", url: "https://www.odtwente.nl/themas/geluid/warmtepompen%2Bairco%2Ben%2Bgeluid/default.aspx" },
      { label: "Achmea Rechtsbijstand: geluidsoverlast warmtepomp of airco", url: "https://www.achmearechtsbijstand.nl/wonen/buren/geluidsoverlast-warmtepomp-airco" },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-buitenunit-plaatsen-regels-limburg", "airco-geluidsoverlast-buren-limburg", "stille-airco-buitenunit-limburg"],
    canonicalSlug: "airco-buitenunit-erfgrens-limburg",
    indexable: true,
  },
  {
    slug: "airco-geluidsoverlast-buren-limburg",
    title: "Airco geluidsoverlast buren Limburg",
    metaTitle: "Airco geluidsoverlast buren Limburg",
    metaDescription: "Airco plaatsen zonder gedoe met buren in Limburg? Vraag advies over stille buitenunits en slimme montage.",
    h1: "Airco plaatsen zonder geluidsoverlast voor buren in Limburg",
    intentType: "rules",
    category: "rules",
    primaryKeyword: "Airco geluidsoverlast buren Limburg",
    secondaryKeywords: ["airco buren geluid", "buitenunit geluidsoverlast", "stille airco buren"],
    intro: "Wil je een airco laten plaatsen, maar maak je je zorgen over geluid bij buren? Vraag advies over stille modellen, demping en een verstandige buitenunitpositie.",
    benefits: ["Stille oplossing kiezen", "Buren vooraf meenemen", "Vrijblijvend advies"],
    whyTitle: "Waarom geluid vooraf serieus nemen?",
    whyBody: "Geluid van een buitenunit hangt af van model, vermogen, plaatsing, afstand, reflectie en trillingen. Een rustige montageplek voorkomt veel discussie achteraf.",
    costTitle: "Wat kost geluid beperken?",
    costBody: "Soms is een stille buitenunit genoeg. In andere situaties zijn dempers, een andere positie of extra leidingwerk nodig. Dat wordt duidelijk in de offerte.",
    processTitle: "Hoe werkt advies over geluid?",
    attentionTitle: "Waar moet je op letten?",
    attentionPoints: ["Vergelijk geluidsgegevens van modellen.", "Let op nachtstand of stille modus.", "Vermijd montage aan lichte of trillingsgevoelige wanden.", "Bespreek buren, erfgrens en slaapkamerraam vooraf."],
    faq: [
      { question: "Zijn moderne buitenunits stil?", answer: "Veel moderne buitenunits zijn relatief stil, maar plaatsing en omgeving bepalen hoe geluid wordt ervaren." },
      { question: "Helpen trillingsdempers?", answer: "Ja, vooral bij wand-, dak- of balkonmontage kunnen dempers resonantie verminderen." },
      { question: "Kan ik geluidsoverlast volledig uitsluiten?", answer: "Volledig uitsluiten kan niet altijd, maar een goede keuze van plek en model verkleint het risico sterk." },
      { question: "Moet ik mijn buren informeren?", answer: "Dat is vaak verstandig, zeker als de buitenunit dicht bij de erfgrens of slaapkamerraam komt." },
    ],
    sourceLinks: [
      { label: "Achmea Rechtsbijstand: geluidsoverlast door buitenunit", url: "https://www.achmearechtsbijstand.nl/wonen/buren/geluidsoverlast-warmtepomp-airco" },
      { label: "Omgevingsdienst Twente: geluid en airco", url: "https://www.odtwente.nl/themas/geluid/warmtepompen%2Bairco%2Ben%2Bgeluid/default.aspx" },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["stille-airco-buitenunit-limburg", "airco-buitenunit-erfgrens-limburg", "airco-zonder-overlast-plaatsen-limburg"],
    canonicalSlug: "airco-geluidsoverlast-buren-limburg",
    indexable: true,
  },
  {
    slug: "stille-airco-buitenunit-limburg",
    title: "Stille airco buitenunit Limburg",
    metaTitle: "Stille airco buitenunit Limburg",
    metaDescription: "Stille airco buitenunit in Limburg nodig? Vraag advies over geluidsarme modellen en plaatsing zonder overlast.",
    h1: "Stille airco buitenunit in Limburg",
    intentType: "rules",
    category: "rules",
    primaryKeyword: "Stille airco buitenunit Limburg",
    secondaryKeywords: ["stille buitenunit airco", "geluidsarme airco buitenunit", "airco buitenunit stil"],
    intro: "Zoek je een stille airco buitenunit in Limburg? Vraag vrijblijvend advies over geluidsarme modellen, montageplek en maatregelen tegen trillingen of hinder.",
    benefits: ["Geluidsarme keuze", "Plaatsing op maat", "Duidelijke offerte"],
    whyTitle: "Waarom kiezen voor een stille buitenunit?",
    whyBody: "Een stille buitenunit is belangrijk bij rijwoningen, appartementen, balkons en kleine tuinen. Het juiste model helpt, maar de montageplek blijft net zo belangrijk.",
    costTitle: "Wat kost een stille airco buitenunit?",
    costBody: "Geluidsarme systemen kunnen duurder zijn, maar voorkomen mogelijk extra maatregelen achteraf. De totale prijs hangt af van toestel, montage en leidingwerk.",
    processTitle: "Hoe werkt advies voor een stille buitenunit?",
    attentionTitle: "Waar moet je op letten?",
    attentionPoints: ["Vraag naar geluidsniveau in normale en stille stand.", "Let op buitenunitpositie ten opzichte van buren.", "Gebruik stevige montage en dempers.", "Kies voldoende vermogen zodat de unit niet continu maximaal draait."],
    faq: [
      { question: "Welke buitenunit is het stilst?", answer: "Dat hangt af van merk, vermogen en gebruik. Een installateur kan modellen vergelijken op geluidsniveau en toepassing." },
      { question: "Is een grotere unit stiller?", answer: "Niet automatisch. Een passend gedimensioneerd systeem draait vaak rustiger dan een te licht systeem." },
      { question: "Maakt dakmontage meer geluid?", answer: "Dat kan door trillingen of resonantie. Goede onderconstructie en demping zijn belangrijk." },
      { question: "Kan een stille unit dicht bij buren staan?", answer: "Ook bij stille units moet de plek zorgvuldig worden beoordeeld." },
    ],
    sourceLinks: [
      { label: "Omgevingsdienst Twente: rekening houden met geluid", url: "https://www.odtwente.nl/themas/geluid/warmtepompen%2Bairco%2Ben%2Bgeluid/default.aspx" },
      { label: "Geluidslabel kenniscentrum", url: "https://www.geluidslabel.com/kenniscentrum/warmtepompen-aircos" },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-geluidsoverlast-buren-limburg", "airco-buitenunit-plaatsen-regels-limburg", "airco-met-buitenunit-plaatsen-limburg"],
    canonicalSlug: "stille-airco-buitenunit-limburg",
    indexable: true,
  },
  {
    slug: "airco-vergunning-nodig-limburg",
    title: "Airco vergunning nodig Limburg",
    metaTitle: "Airco vergunning nodig Limburg",
    metaDescription: "Vergunning nodig voor airco in Limburg? Vraag advies over buitenunit, VvE, gevel, dak en plaatsing.",
    h1: "Heb je een vergunning nodig voor een airco in Limburg?",
    intentType: "rules",
    category: "rules",
    primaryKeyword: "Airco vergunning nodig Limburg",
    secondaryKeywords: ["vergunning airco buitenunit", "airco plaatsen vergunning", "airco VvE toestemming"],
    intro: "Vraag je je af of je een vergunning of toestemming nodig hebt voor een airco in Limburg? Laat je situatie vooraf beoordelen, zeker bij gevel, dak, balkon of VvE.",
    benefits: ["Voorkom vertraging", "Check gebouwsituatie", "Vrijblijvend advies"],
    whyTitle: "Waarom vergunning of toestemming checken?",
    whyBody: "Bij veel woningen kan een airco zonder vergunning, maar uitzonderingen bestaan. Denk aan appartementen, monumenten, beschermd stadsgezicht, VvE-regels of zichtbare gevelplaatsing.",
    costTitle: "Wat kost extra voorbereiding?",
    costBody: "Een check kost vooral tijd, maar kan dure verplaatsing voorkomen. Soms vraagt een andere montageplek of extra afwerking om meerwerk.",
    processTitle: "Hoe werkt een vergunning-check?",
    attentionTitle: "Waar moet je op letten?",
    attentionPoints: ["Controleer VvE-reglement bij appartementen.", "Let op monument of beschermd stadsgezicht.", "Bespreek zichtbaarheid van de buitenunit.", "Vraag gemeente of installateur bij twijfel om advies."],
    faq: [
      { question: "Is een airco altijd vergunningsvrij?", answer: "Nee, dat hangt af van woningtype, locatie, zichtbaarheid en lokale of gebouwgebonden regels." },
      { question: "Heb ik bij een appartement toestemming nodig?", answer: "Vaak wel via VvE of verhuurder, zeker voor buitengevel, balkon of dak." },
      { question: "Kan een installateur dit controleren?", answer: "Een installateur kan praktisch meedenken, maar formele toestemming ligt bij eigenaar, VvE of gemeente." },
      { question: "Wat als ik zonder toestemming plaats?", answer: "Dan loop je risico op klachten of het moeten aanpassen/verplaatsen van de installatie." },
    ],
    sourceLinks: [
      { label: "Omgevingsloket: vergunningcheck", url: "https://www.omgevingsloket.nl/" },
      { label: "IPLO: Omgevingsloket en vergunningcheck", url: "https://iplo.nl/digitaal-stelsel/storingen-onderhoud-release-informatie/release-informatie/2025/grote-veranderingen-vergunningcheck-1-juli-2025/" },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-buitenunit-plaatsen-regels-limburg", "airco-buitenunit-op-balkon-limburg", "airco-buitenunit-op-dak-limburg"],
    canonicalSlug: "airco-vergunning-nodig-limburg",
    indexable: true,
  },
  {
    slug: "airco-buitenunit-op-dak-limburg",
    title: "Airco buitenunit op dak Limburg",
    metaTitle: "Airco buitenunit op dak Limburg",
    metaDescription: "Airco buitenunit op een plat dak in Limburg? Vraag advies over montage, geluid, trillingen en bereikbaarheid.",
    h1: "Airco buitenunit op dak plaatsen in Limburg",
    intentType: "rules",
    category: "rules",
    primaryKeyword: "Airco buitenunit op dak Limburg",
    secondaryKeywords: ["airco buitenunit plat dak", "dakmontage airco buitenunit", "airco op dakkapel"],
    intro: "Een airco buitenunit op een dak kan praktisch zijn, maar vraagt aandacht voor draagkracht, trillingen, geluid en onderhoud. Vraag advies voor dakmontage in Limburg.",
    benefits: ["Dakmontage laten beoordelen", "Minder zichtbare plaatsing", "Duidelijke montage-offerte"],
    whyTitle: "Waarom dakmontage goed laten beoordelen?",
    whyBody: "Een dakopstelling kan ruimte besparen, maar verkeerde montage kan trillingen, lekkage of geluid veroorzaken. Ook veilige bereikbaarheid voor onderhoud telt mee.",
    costTitle: "Wat kost een buitenunit op het dak?",
    costBody: "De prijs hangt af van daktype, bereikbaarheid, leidinglengte, onderconstructie en demping. Soms is dakmontage duurder dan gevel- of grondplaatsing.",
    processTitle: "Hoe werkt advies over dakmontage?",
    attentionTitle: "Waar moet je op letten?",
    attentionPoints: ["Controleer draagkracht en dakbedekking.", "Voorkom trillingen in de woning.", "Zorg voor veilige bereikbaarheid.", "Bespreek condensafvoer en leidingroute."],
    faq: [
      { question: "Kan een buitenunit op elk plat dak?", answer: "Nee, dakconstructie, dakbedekking en bereikbaarheid moeten geschikt zijn." },
      { question: "Geeft dakmontage trillingen?", answer: "Dat kan. Daarom zijn goede dempers en een geschikte onderconstructie belangrijk." },
      { question: "Is dakmontage duurder?", answer: "Vaak wel als bereikbaarheid, veiligheidsmaatregelen of extra leidinglengte nodig zijn." },
      { question: "Kan dit ook op een dakkapel?", answer: "Alleen als constructie, geluid en trillingen verantwoord zijn. Laat dit altijd beoordelen." },
    ],
    sourceLinks: [
      { label: "Omgevingsdienst Twente: geluid van buitenunits", url: "https://www.odtwente.nl/themas/geluid/warmtepompen%2Bairco%2Ben%2Bgeluid/default.aspx" },
      { label: "Omgevingsloket: vergunningcheck", url: "https://www.omgevingsloket.nl/" },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-buitenunit-plaatsen-regels-limburg", "stille-airco-buitenunit-limburg", "airco-geluidsoverlast-buren-limburg"],
    canonicalSlug: "airco-buitenunit-op-dak-limburg",
    indexable: true,
  },
  {
    slug: "airco-buitenunit-op-balkon-limburg",
    title: "Airco buitenunit op balkon Limburg",
    metaTitle: "Airco buitenunit op balkon Limburg",
    metaDescription: "Airco buitenunit op balkon in Limburg? Vraag advies over VvE, geluid, condens, ruimte en montage.",
    h1: "Airco buitenunit op balkon plaatsen in Limburg",
    intentType: "rules",
    category: "rules",
    primaryKeyword: "Airco buitenunit op balkon Limburg",
    secondaryKeywords: ["airco balkon appartement", "buitenunit balkon VvE", "airco appartement buitenunit"],
    intro: "Een airco buitenunit op een balkon vraagt extra aandacht voor VvE-regels, geluid, condenswater, luchtstroming en ruimte. Vraag advies voor je appartement in Limburg.",
    benefits: ["Geschikt voor appartementen", "VvE-risico vooraf checken", "Praktische plaatsingskeuze"],
    whyTitle: "Waarom balkonplaatsing extra gevoelig is",
    whyBody: "Op een balkon zit de buitenunit vaak dicht bij buren en gevels. Daardoor zijn geluid, warmteafvoer, trillingen en zichtbaarheid belangrijker dan bij een vrije tuinopstelling.",
    costTitle: "Wat kost balkonmontage?",
    costBody: "De kosten hangen af van montageconstructie, leidingroute, condensafvoer, bereikbaarheid en eventuele VvE-eisen. Een offerte geeft helderheid.",
    processTitle: "Hoe werkt advies voor balkonplaatsing?",
    attentionTitle: "Waar moet je op letten?",
    attentionPoints: ["Vraag toestemming aan VvE of verhuurder.", "Let op vrije luchtuitblaas.", "Voorkom condenswateroverlast.", "Kies een stille unit met goede demping."],
    faq: [
      { question: "Mag een buitenunit op een balkon?", answer: "Dat hangt af van VvE, verhuurder, gebouwregels en technische geschiktheid." },
      { question: "Is een balkonunit zichtbaar vanaf buiten?", answer: "Vaak wel. Dat kan invloed hebben op toestemming of gewenste afwerking." },
      { question: "Kan condenswater problemen geven?", answer: "Ja, condensafvoer moet netjes worden opgelost zodat buren of gevel geen overlast ervaren." },
      { question: "Is een airco zonder buitenunit beter voor een appartement?", answer: "Soms wel, maar comfort, geluid en rendement verschillen. Laat beide opties vergelijken." },
    ],
    sourceLinks: [
      { label: "Omgevingsloket: vergunningcheck", url: "https://www.omgevingsloket.nl/" },
      { label: "Omgevingsdienst Twente: airco en geluid", url: "https://www.odtwente.nl/themas/geluid/warmtepompen%2Bairco%2Ben%2Bgeluid/default.aspx" },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-voor-appartement-limburg", "airco-zonder-buitenunit-limburg", "airco-vergunning-nodig-limburg"],
    canonicalSlug: "airco-buitenunit-op-balkon-limburg",
    indexable: true,
  },
  {
    slug: "airco-zonder-overlast-plaatsen-limburg",
    title: "Airco zonder overlast plaatsen Limburg",
    metaTitle: "Airco zonder overlast Limburg",
    metaDescription: "Airco zonder overlast laten plaatsen in Limburg? Vraag advies over geluid, plaatsing en nette montage.",
    h1: "Airco zonder overlast laten plaatsen in Limburg",
    intentType: "rules",
    category: "rules",
    primaryKeyword: "Airco zonder overlast plaatsen Limburg",
    secondaryKeywords: ["airco plaatsen zonder burenlast", "stille airco installatie", "airco nette montage"],
    intro: "Wil je een airco laten plaatsen zonder gedoe over geluid, trillingen of zichtbare leidingen? Vraag vrijblijvend advies over een nette en rustige installatie in Limburg.",
    benefits: ["Rustige montage", "Nette afwerking", "Voorkom klachten"],
    whyTitle: "Waarom overlast vooraf voorkomen?",
    whyBody: "Overlast ontstaat vaak door een verkeerde buitenunitplek, slechte demping, onhandige leidingroute of onduidelijke verwachtingen. Een goede voorbereiding voorkomt dat.",
    costTitle: "Wat kost overlastarm plaatsen?",
    costBody: "Soms zijn extra dempers, langere leidingen of betere afwerking nodig. De offerte laat zien welke maatregelen logisch zijn voor jouw woning.",
    processTitle: "Hoe werkt een overlastarme aanvraag?",
    attentionTitle: "Waar moet je op letten?",
    attentionPoints: ["Kies een logische plek voor binnen- en buitenunit.", "Beperk zichtbare leidingen waar mogelijk.", "Gebruik trillingsdemping.", "Bespreek werktijden en montageplanning."],
    faq: [
      { question: "Kan installatie zonder rommel?", answer: "Een nette installateur werkt stofarm en stemt leidingroutes vooraf af." },
      { question: "Kan een buitenunit trillen?", answer: "Ja, daarom zijn stevige montage en dempers belangrijk." },
      { question: "Hoe voorkom ik klachten van buren?", answer: "Kies een stille unit, goede plek en informeer buren bij gevoelige situaties." },
      { question: "Kan ik vooraf foto's meesturen?", answer: "Ja, dat helpt om de situatie sneller en beter in te schatten." },
    ],
    sourceLinks: [
      { label: "Achmea Rechtsbijstand: buren en geluidsoverlast", url: "https://www.achmearechtsbijstand.nl/wonen/buren/geluidsoverlast-warmtepomp-airco" },
      { label: "Omgevingsdienst Twente: geluid voorkomen", url: "https://www.odtwente.nl/themas/geluid/warmtepompen%2Bairco%2Ben%2Bgeluid/default.aspx" },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-geluidsoverlast-buren-limburg", "stille-airco-buitenunit-limburg", "airco-buitenunit-plaatsen-regels-limburg"],
    canonicalSlug: "airco-zonder-overlast-plaatsen-limburg",
    indexable: true,
  },
  {
    slug: "airco-installatie-f-gassen-gecertificeerd-limburg",
    title: "Airco installatie F-gassen gecertificeerd Limburg",
    metaTitle: "F-gassen airco installatie Limburg",
    metaDescription: "Airco laten installeren door F-gassen gecertificeerde vakmensen in Limburg? Vraag vrijblijvend advies aan.",
    h1: "Airco installatie door F-gassen gecertificeerde vakmensen in Limburg",
    intentType: "rules",
    category: "rules",
    primaryKeyword: "Airco installatie F-gassen gecertificeerd Limburg",
    secondaryKeywords: ["F-gassen gecertificeerde airco installateur", "split airco gecertificeerd installeren", "airco monteur certificaat"],
    intro: "Een split airco installeren vraagt koeltechnische kennis en de juiste certificering. Vraag vrijblijvend advies aan voor een veilige en correcte installatie in Limburg.",
    benefits: ["Correcte installatie", "Minder risico op lekkage", "Duidelijke vervolgstap"],
    whyTitle: "Waarom F-gassen certificering belangrijk is",
    whyBody: "Bij split airco's kunnen koeltechnische handelingen nodig zijn. Een gecertificeerde vakman werkt veilig, beperkt lekkagerisico en voorkomt problemen met garantie of regelgeving.",
    costTitle: "Wat kost gecertificeerde installatie?",
    costBody: "De kosten hangen af van systeem, leidingwerk en montageplek. Gecertificeerd werk is onderdeel van een nette totaalofferte voor installatie.",
    processTitle: "Hoe werkt een aanvraag voor gecertificeerde installatie?",
    attentionTitle: "Waar moet je op letten?",
    attentionPoints: ["Vraag of koeltechnisch werk gecertificeerd wordt uitgevoerd.", "Controleer wat in de montageprijs zit.", "Bespreek garantie op installatie.", "Laat geen split airco zonder juiste kennis aansluiten."],
    faq: [
      { question: "Mag iedereen een split airco installeren?", answer: "Voor koeltechnische handelingen gelden certificeringseisen. Laat dit daarom professioneel uitvoeren." },
      { question: "Waarom is vacumeren belangrijk?", answer: "Correct vacumeren helpt vocht en lucht uit het systeem verwijderen en voorkomt storingen." },
      { question: "Kan ik zelf een airco kopen?", answer: "Dat kan, maar installatie en garantie zijn vaak duidelijker als toestel en montage samen worden geregeld." },
      { question: "Is gecertificeerde installatie duurder?", answer: "Het is vooral veiliger en betrouwbaarder. De totaalprijs hangt af van jouw situatie." },
    ],
    sourceLinks: [
      { label: "Wasco: F-gassenregelgeving", url: "https://www.wasco.nl/content/f-gassen-regelgeving" },
      { label: "ILT jaarverslag: verkoop en installatie split-unit airco's", url: "https://www.ilent.nl/site/binaries/site-content/collections/documents/2022/05/19/jaarverslag-2021-in-pdf/Jaarverslag%2BILT%2B2021.pdf" },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["zelf-airco-installeren-of-laten-doen-limburg", "airco-installatie-limburg", "airco-inclusief-montage-limburg"],
    canonicalSlug: "airco-installatie-f-gassen-gecertificeerd-limburg",
    indexable: true,
  },
  {
    slug: "zelf-airco-installeren-of-laten-doen-limburg",
    title: "Zelf airco installeren of laten doen Limburg",
    metaTitle: "Zelf airco installeren Limburg?",
    metaDescription: "Zelf airco installeren of laten doen in Limburg? Vergelijk risico's, regels, garantie en montagekosten.",
    h1: "Zelf airco installeren of laten doen in Limburg?",
    intentType: "rules",
    category: "rules",
    primaryKeyword: "Zelf airco installeren of laten doen Limburg",
    secondaryKeywords: ["zelf split airco installeren", "airco laten installeren Limburg", "airco installatie risico"],
    intro: "Twijfel je tussen zelf een airco installeren of laten doen in Limburg? Vraag advies over risico's, regels, garantie, koeltechniek en de echte montagekosten.",
    benefits: ["Voorkom dure fouten", "Heldere vergelijking", "Vrijblijvend advies"],
    whyTitle: "Waarom zelf installeren kritisch bekijken?",
    whyBody: "Een airco lijkt soms eenvoudig, maar fout leidingwerk, lekkage, verkeerde plek of onjuiste aansluiting kan rendement, levensduur en garantie schaden.",
    costTitle: "Wat kost laten doen vergeleken met zelf doen?",
    costBody: "Zelf doen lijkt goedkoper, maar extra materiaal, gereedschap, foutkansen en garantie tellen mee. Een offerte maakt de vergelijking eerlijker.",
    processTitle: "Hoe werkt advies over zelf doen of laten doen?",
    attentionTitle: "Waar moet je op letten?",
    attentionPoints: ["Controleer certificeringseisen voor split airco's.", "Let op garantievoorwaarden.", "Neem elektra, leidingwerk en condensafvoer mee.", "Vergelijk totale kosten in plaats van alleen toestelprijs."],
    faq: [
      { question: "Mag ik zelf een split airco installeren?", answer: "Niet alle handelingen mag of moet je zelf uitvoeren. Koeltechnische werkzaamheden vragen de juiste certificering." },
      { question: "Verlies ik garantie bij zelf installeren?", answer: "Dat kan. Veel fabrikanten en leveranciers stellen eisen aan montage en inbedrijfstelling." },
      { question: "Is een voorgevulde airco makkelijker?", answer: "Voorgevuld betekent niet automatisch risicoloos. Aansluiting, vacumeren en controle blijven belangrijk." },
      { question: "Wanneer is laten installeren verstandiger?", answer: "Bij split airco's, meerdere ruimtes, dak- of balkonmontage en twijfel over vermogen of buitenunitplek." },
    ],
    sourceLinks: [
      { label: "Wasco: F-gassenregelgeving", url: "https://www.wasco.nl/content/f-gassen-regelgeving" },
      { label: "ILT: aandacht voor gecertificeerde installatie", url: "https://www.ilent.nl/site/binaries/site-content/collections/documents/2022/05/19/jaarverslag-2021-in-pdf/Jaarverslag%2BILT%2B2021.pdf" },
    ],
    ctaVariant: "advies",
    relatedSlugs: ["airco-installatie-f-gassen-gecertificeerd-limburg", "airco-installatie-limburg", "airco-kopen-en-laten-installeren-limburg"],
    canonicalSlug: "zelf-airco-installeren-of-laten-doen-limburg",
    indexable: true,
  },
];

export const servicePagesBySlug = servicePages.reduce<Record<string, ServicePage>>((acc, page) => {
  acc[page.slug] = page;
  return acc;
}, {});

export function getServicePageBySlug(slug: string | undefined): ServicePage | undefined {
  if (!slug) return undefined;
  return servicePagesBySlug[slug];
}
